import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Listbox, Portal, Switch, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import {
  BellIcon,
  CalendarIcon,
  CashIcon,
  ChevronDownIcon,
  ClipboardCheckIcon,
  ClockIcon,
  DocumentTextIcon,
  MailIcon,
  PencilIcon,
  PhoneIcon,
  PlusIcon,
  TagIcon,
  TrashIcon,
  XIcon,
  UserAddIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/outline';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../components/Button';
import TooltipLight from '../../components/TooltipLight';
import ErrorMessage from '../../components/ErrorMessage';
import { logger } from '../../../lib/logger';
import {
  MeProviderProgramTagsDocument,
  PatientLifecycleStatusV2,
  useCreateProgramTagMutation,
  useFollowUpTemplatesQuery,
  useProviderAssessmentsWithoutScheduleQuery,
  useMeProviderProgramTagsQuery,
  usePatientsV2NamesQuery,
  useProviderServicesQuery,
  useProviderUserMembersV2Query,
} from '../../../generated/graphql';
import {
  emailRegisterOptions,
  linkRegisterOptions,
  phoneRegisterOptions,
} from '../../lib/register-options';
import { getTagStyles } from '../../lib/colors';
import Tag from '../../svgs/Tag';
import classNames from 'classnames';
import {
  ACTION_SCHEMAS_FOR_BUILDER,
  ACTION_SCHEMA_BY_TYPE,
  CONDITION_FIELDS_BY_TRIGGER,
  DEFAULT_CONDITION_OPERATORS,
  DEFAULT_TAG_COLOR,
  OPERATORS_BY_VALUE_TYPE,
  OPERATOR_LABELS,
  TRIGGER_OPTIONS,
  TRIGGER_OPTIONS_FOR_BUILDER,
  TRIGGER_OPTIONS_COMING_SOON,
  ACTION_SCHEMAS_COMING_SOON,
  DEFAULT_TIME_TRIGGER_CONFIG,
  formatTagNameList,
  isTimeBasedTrigger,
  isValidTimeString,
  parseTimeTriggerConfig,
  TIME_TRIGGER_OFFSET_MAX,
} from './automationMetadata';
import OffsetControl from './OffsetControl';
import { RULE_NAME_MAX_LENGTH } from './constants';
import { AutomationRulePartial } from './types';
import { getWorkflowTriggerLabel, getWorkflowActionDisplays } from './RuleList';
import { ActionBadge, TimeBadge, TriggerBadge } from './RuleListComponents';
import { getTimezoneAbbreviation, DEFAULT_MAIL_TIMEZONE } from '../../lib/time';

const MAX_ACTIONS = 10;

/** Icons for each action type in the action dropdown. */
const ACTION_TYPE_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  assign_tag: TagIcon,
  send_notification: BellIcon,
  send_email: MailIcon,
  send_sms: PhoneIcon,
  send_template: MailIcon,
  send_contact_form: DocumentTextIcon,
  update_field: PencilIcon,
  create_invoice: CashIcon,
};

/** Icons for each trigger event in the trigger dropdown. */
const TRIGGER_EVENT_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  assessment_completed: ClipboardCheckIcon,
  assessment_submitted: ClipboardCheckIcon,
  appointment_scheduled: CalendarIcon,
  appointment_missed: CalendarIcon,
  session_completed: CalendarIcon,
  client_tag_added: TagIcon,
  client_tag_removed: TagIcon,
  client_created: UserAddIcon,
  invoice_paid: CashIcon,
  practitioner_assigned: UserGroupIcon,
  service_booked: CalendarIcon,
  // Time-based triggers
  program_start_date: CalendarIcon,
  program_end_date: CalendarIcon,
};

interface RuleEditorProps {
  rule: AutomationRulePartial | null;
  onChange: (rule: AutomationRulePartial) => void;
  onSave: (rule: AutomationRulePartial) => void;
  onCancel: () => void;
  updating: boolean;
  saveError: string | null;
  /** Called when a tag is created in the editor; parent can use for RuleList display before cache refetch */
  onTagCreated?: (tag: {
    id: string;
    name: string;
    color?: string | null;
  }) => void;
}

export default function RuleEditor({
  rule,
  onChange,
  onSave,
  onCancel,
  updating,
  saveError,
  onTagCreated: onTagCreatedFromParent,
}: RuleEditorProps) {
  const TAG_TRIGGER_EVENTS = ['client_tag_added', 'client_tag_removed'];
  const SERVICE_TRIGGER_EVENTS = ['service_booked'];
  const PRACTITIONER_TRIGGER_EVENTS = ['practitioner_assigned'];
  const [conditionRows, setConditionRows] = useState<
    Array<{ field: string; operator: string; value: string }>
  >([]);
  const [actionRows, setActionRows] = useState<
    Array<{ type: string; params: Record<string, string> }>
  >([]);
  const [saveValidationErrors, setSaveValidationErrors] = useState<
    Record<string, string>
  >({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  const registerBlur = (key: string) => () =>
    setTouchedFields((prev) => new Set(prev).add(key));

  const { data: programTagsData } = useMeProviderProgramTagsQuery();
  const { data: providerServicesData, error: providerServicesError } =
    useProviderServicesQuery();
  const { data: providerMembersData } = useProviderUserMembersV2Query({
    variables: { activeProviderUsersOnly: true },
  });
  const { data: patientsData } = usePatientsV2NamesQuery({
    variables: {
      patientLifecycleStatuses: [PatientLifecycleStatusV2.Active],
      assignedPatients: true,
    },
  });
  const [createProgramTagMutation] = useCreateProgramTagMutation();
  const { data: providerAssessmentsData, loading: formsLoading } =
    useProviderAssessmentsWithoutScheduleQuery({
      fetchPolicy: 'cache-first',
    });
  const practitionerOnlyForms = useMemo(
    () =>
      (providerAssessmentsData?.forms ?? []).filter(
        (f) => f.completableByProviderOnly === true,
      ),
    [providerAssessmentsData?.forms],
  );

  const { data: followUpTemplatesData } = useFollowUpTemplatesQuery({
    variables: { input: { isDraft: false } },
    fetchPolicy: 'cache-first',
  });
  const mailTemplates = followUpTemplatesData?.followUpTemplates ?? [];
  const { authedProviderUser } = useAuth();
  const hasStripeConnected = Boolean(
    authedProviderUser?.provider?.stripeAccountId,
  );

  const programTagsFromQuery =
    programTagsData?.meProvider?.provider.programTags || [];
  const [newlyCreatedTags, setNewlyCreatedTags] = useState<
    Record<string, { id: string; name: string; color?: string | null }>
  >({});
  const programTags = useMemo(() => {
    const created = Object.values(newlyCreatedTags);
    const existing = programTagsFromQuery;
    return [...created, ...existing.filter((t) => !newlyCreatedTags[t.id])];
  }, [programTagsFromQuery, newlyCreatedTags]);
  const providerServices = providerServicesData?.providerServices || [];
  const providerUsers = providerMembersData?.providerUserMembers ?? [];
  const patients = patientsData?.patientNames ?? [];
  const isTagTriggerEvent = TAG_TRIGGER_EVENTS.includes(
    rule?.triggerEvent || '',
  );
  const isServiceTriggerEvent = SERVICE_TRIGGER_EVENTS.includes(
    rule?.triggerEvent || '',
  );
  const isPractitionerTriggerEvent = PRACTITIONER_TRIGGER_EVENTS.includes(
    rule?.triggerEvent || '',
  );
  const isTimeTriggerEvent = isTimeBasedTrigger(rule?.triggerEvent || '');

  const triggerAnchorLabel = isTimeTriggerEvent
    ? rule?.triggerEvent === 'program_end_date'
      ? 'end'
      : 'start'
    : '';

  const triggerAnchorLabelLong = isTimeTriggerEvent
    ? rule?.triggerEvent === 'program_end_date'
      ? 'program end date'
      : 'program start date'
    : '';

  const showsGenericConditions =
    !isTagTriggerEvent &&
    !isServiceTriggerEvent &&
    !isPractitionerTriggerEvent &&
    !isTimeTriggerEvent &&
    rule?.triggerEvent !== 'client_created';

  const getConditionFields = (triggerEvent: string) =>
    CONDITION_FIELDS_BY_TRIGGER[triggerEvent] || [];

  const getFieldMeta = (triggerEvent: string, field: string) =>
    getConditionFields(triggerEvent).find((item) => item.key === field);

  const getOperatorOptions = (triggerEvent: string, field: string) => {
    const fieldMeta = getFieldMeta(triggerEvent, field);
    if (!fieldMeta) return DEFAULT_CONDITION_OPERATORS;
    if (fieldMeta.operators?.length) return fieldMeta.operators;
    return (
      OPERATORS_BY_VALUE_TYPE[fieldMeta.valueType] ||
      DEFAULT_CONDITION_OPERATORS
    );
  };

  const getDefaultConditionField = (triggerEvent: string) => {
    const fields = getConditionFields(triggerEvent);
    return fields[0]?.key || '';
  };

  const getDefaultOperator = (triggerEvent: string, field: string) =>
    getOperatorOptions(triggerEvent, field)[0] || 'eq';

  const getDefaultActionParams = (
    actionType: string,
    existingParams?: Record<string, string>,
  ): Record<string, string> => {
    const actionSchema = ACTION_SCHEMA_BY_TYPE[actionType];
    if (!actionSchema) {
      return existingParams || {};
    }

    return actionSchema.params.reduce((acc, param) => {
      const existingValue = existingParams?.[param.key];
      if (existingValue !== undefined) {
        acc[param.key] = existingValue;
        return acc;
      }

      if (param.options?.length) {
        acc[param.key] = param.options[0].value;
        return acc;
      }

      acc[param.key] = '';
      return acc;
    }, {} as Record<string, string>);
  };

  const parseTagIds = (value?: string) =>
    (value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

  const parsedTriggerConfig = useMemo(
    () => parseTimeTriggerConfig(rule?.triggerConfig),
    [rule?.triggerConfig],
  );

  const effectiveTriggerConfig = parsedTriggerConfig ?? {
    ...DEFAULT_TIME_TRIGGER_CONFIG,
  };

  const normalizeServiceConditionRows = (
    rules: Array<{ field: string; operator: string; value: string }>,
  ) => {
    return rules
      .filter(
        (r) =>
          r.field === 'provider_service_id' &&
          (r.value ?? '').trim().length > 0,
      )
      .map((r) => ({
        field: 'provider_service_id' as const,
        operator: 'contains' as const,
        value: r.value.trim(),
      }));
  };

  const normalizeTagConditionRows = (
    triggerEvent: string,
    rules: Array<{ field: string; operator: string; value: string }>,
  ) => {
    if (!TAG_TRIGGER_EVENTS.includes(triggerEvent)) {
      return rules;
    }

    return rules.flatMap((condition) => {
      if (
        condition.field !== 'client_tag_ids' &&
        condition.field !== 'tag_id' &&
        condition.field !== 'tag_name'
      ) {
        return condition;
      }

      // New format: tag_id with eq operator (correct for tag-triggered events)
      if (condition.field === 'tag_id') {
        return {
          field: 'tag_id',
          operator: 'eq',
          value: condition.value,
        };
      }

      const rawValues = parseTagIds(condition.value);
      const values = rawValues.length
        ? rawValues
        : [condition.value].filter(Boolean);
      if (!values.length) {
        return [];
      }

      return values.map((rawValue) => {
        if (condition.field === 'tag_name') {
          const matchedTag = programTags.find(
            (tag) => tag.name.toLowerCase() === rawValue.toLowerCase(),
          );
          return {
            field: 'tag_id',
            operator: 'eq',
            value: matchedTag?.id || rawValue,
          };
        }

        // Legacy format: client_tag_ids with contains operator (backwards compatibility for old rules)
        return {
          field: 'tag_id',
          operator: 'eq',
          value: rawValue,
        };
      });
    });
  };

  const normalizeTimeTriggerConditionRows = (
    rules: Array<{ field: string; operator: string; value: string }>,
  ) => {
    const seen = new Set<string>();
    const normalized: Array<{
      field: string;
      operator: string;
      value: string;
    }> = [];
    for (const ruleRow of rules) {
      if (
        (ruleRow.field !== 'client_tag_ids' && ruleRow.field !== 'tag_id') ||
        !(ruleRow.value ?? '').trim()
      ) {
        continue;
      }
      for (const tagId of parseTagIds(ruleRow.value)) {
        if (seen.has(tagId)) {
          continue;
        }
        seen.add(tagId);
        normalized.push({
          field: 'client_tag_ids',
          operator: 'contains',
          value: tagId,
        });
      }
    }
    return normalized;
  };

  const normalizePractitionerConditionRows = (
    rules: Array<{ field: string; operator: string; value: string }>,
  ) => {
    const practitionerRule = rules.find(
      (r) => r.field === 'assigned_provider_user_id' && (r.value ?? '').trim(),
    );
    const patientRule = rules.find(
      (r) => r.field === 'patient_id' && (r.value ?? '').trim(),
    );
    const result: Array<{
      field: string;
      operator: string;
      value: string;
    }> = [];
    if (practitionerRule) {
      result.push({
        field: 'assigned_provider_user_id',
        operator: 'eq',
        value: practitionerRule.value.trim(),
      });
    }
    if (patientRule) {
      result.push({
        field: 'patient_id',
        operator: 'eq',
        value: patientRule.value.trim(),
      });
    }
    return result;
  };

  const buildStructuredState = (nextRule: AutomationRulePartial) => {
    const triggerEvent =
      nextRule.triggerEvent ||
      TRIGGER_OPTIONS_FOR_BUILDER[0]?.value ||
      'client_created';
    const incomingConditions = nextRule.conditions || {
      logic: 'AND',
      rules: [],
    };
    const rawRules = (incomingConditions.rules || []).map((condition) => ({
      field: condition.field || '',
      operator: condition.operator || 'eq',
      value: condition.value || '',
    }));
    const incomingRules = SERVICE_TRIGGER_EVENTS.includes(triggerEvent)
      ? normalizeServiceConditionRows(rawRules)
      : PRACTITIONER_TRIGGER_EVENTS.includes(triggerEvent)
      ? normalizePractitionerConditionRows(rawRules)
      : isTimeBasedTrigger(triggerEvent)
      ? normalizeTimeTriggerConditionRows(rawRules)
      : normalizeTagConditionRows(triggerEvent, rawRules);

    const incomingActions = (nextRule.actions || [])
      .filter((action) => action?.type && action.type !== 'call_webhook')
      .map((action) => ({
        type: action.type || '',
        params: (action.params || []).reduce((acc, param) => {
          if (param.key) {
            acc[param.key] = param.value || '';
          }
          return acc;
        }, {} as Record<string, string>),
      }));

    incomingActions.forEach((action) => {
      if (
        action.type === 'assign_tag' &&
        !action.params.tag_ids &&
        action.params.tag_id
      ) {
        action.params.tag_ids = action.params.tag_id;
      }
      if (
        action.type === 'assign_tag' &&
        action.params.entity_id_field &&
        action.params.entity_id_field !== 'program_id'
      ) {
        action.params.entity_id_field = 'program_id';
      }
      if (action.type === 'assign_tag' && !action.params.entity_id_field) {
        action.params.entity_id_field = 'program_id';
      }
    });

    setConditionRows(incomingRules.length > 0 ? incomingRules : []);
    setActionRows(
      incomingActions.length > 0
        ? incomingActions.map((action) => ({
            ...action,
            params: getDefaultActionParams(action.type, action.params),
          }))
        : [
            {
              type: ACTION_SCHEMAS_FOR_BUILDER[0].type,
              params: getDefaultActionParams(
                ACTION_SCHEMAS_FOR_BUILDER[0].type,
              ),
            },
          ],
    );
  };

  // Log provider services query errors for debugging
  useEffect(() => {
    if (providerServicesError) {
      logger.error('Failed to load provider services in automation editor', {
        error: providerServicesError,
      });
    }
  }, [providerServicesError]);

  useEffect(() => {
    if (!rule) return;
    buildStructuredState(rule);
    setSaveValidationErrors({});
  }, [
    rule?.id,
    rule?.updatedAt,
    rule?.triggerEvent,
    rule?.conditions,
    rule?.actions,
  ]);

  useEffect(() => {
    if (!rule) return;
    const availableFields = getConditionFields(rule.triggerEvent || '');
    const availableFieldSet = new Set(
      availableFields.map((field) => field.key),
    );
    if (!availableFields.length) return;

    setConditionRows((previousRows) =>
      (previousRows.length ? previousRows : []).map((row) => {
        const nextField = availableFieldSet.has(row.field)
          ? row.field
          : availableFields[0].key;
        const operatorOptions = getOperatorOptions(
          rule.triggerEvent || '',
          nextField,
        );
        const nextOperator = operatorOptions.includes(row.operator)
          ? row.operator
          : operatorOptions[0] || 'eq';
        const fieldChanged = nextField !== row.field;

        return {
          ...row,
          field: nextField,
          operator: nextOperator,
          value: fieldChanged ? '' : row.value,
        };
      }),
    );
  }, [rule?.triggerEvent]);

  if (!rule) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="text-sm text-neutral-110">
          Select a rule to edit or create a new one.
        </p>
      </div>
    );
  }

  const updateRule = (patch: Partial<AutomationRulePartial>) => {
    onChange({
      ...rule,
      ...patch,
    });
  };

  const buildStructuredPayload = () => {
    const actions = actionRows
      .filter((action) => action.type && action.type !== 'call_webhook')
      .map((action) => {
        const schema = ACTION_SCHEMA_BY_TYPE[action.type];
        if (!schema) {
          return {
            type: action.type.trim(),
            params: Object.entries(action.params).map(([key, value]) => ({
              key,
              value: (value || '').trim(),
            })),
          };
        }

        const normalizedParams = { ...action.params };
        if (action.type === 'assign_tag') {
          if (!normalizedParams.tag_ids && normalizedParams.tag_id) {
            normalizedParams.tag_ids = normalizedParams.tag_id;
          }
          if (!normalizedParams.entity_id_field) {
            normalizedParams.entity_id_field = 'program_id';
          }
        }

        return {
          type: action.type.trim(),
          params: schema.params
            .map((param) => ({
              key: param.key,
              value: (normalizedParams[param.key] || '').trim(),
              required: Boolean(param.required),
            }))
            .filter((param) => param.required || param.value.length > 0)
            .map(({ key, value }) => ({ key, value })),
        };
      });

    // Time-based triggers may have optional live "client has tag" AND conditions
    if (isTimeTriggerEvent) {
      return {
        conditions: {
          logic: 'AND' as const,
          rules: normalizeTimeTriggerConditionRows(
            conditionRows.map((row) => ({
              field: row.field.trim(),
              operator: row.operator.trim(),
              value: row.value.trim(),
            })),
          ),
        },
        actions,
      };
    }

    const normalizedConditionRows = SERVICE_TRIGGER_EVENTS.includes(
      rule.triggerEvent || '',
    )
      ? normalizeServiceConditionRows(
          conditionRows.map((row) => ({
            field: row.field.trim(),
            operator: row.operator.trim(),
            value: row.value.trim(),
          })),
        )
      : PRACTITIONER_TRIGGER_EVENTS.includes(rule.triggerEvent || '')
      ? normalizePractitionerConditionRows(
          conditionRows.map((row) => ({
            field: row.field.trim(),
            operator: row.operator.trim(),
            value: row.value.trim(),
          })),
        )
      : TAG_TRIGGER_EVENTS.includes(rule.triggerEvent || '')
      ? normalizeTagConditionRows(
          rule.triggerEvent || '',
          conditionRows.map((row) => ({
            field: row.field.trim(),
            operator: row.operator.trim(),
            value: row.value.trim(),
          })),
        )
      : conditionRows
          .filter((row) => row.field.trim() && row.value?.trim())
          .map((row) => ({
            field: row.field.trim(),
            operator: row.operator.trim(),
            value: row.value.trim(),
          }));

    return {
      conditions: {
        logic: 'AND' as const,
        rules: normalizedConditionRows,
      },
      actions,
    };
  };

  const validateTypedValue = (
    value: string,
    valueType: string,
    isOptional = false,
  ): string | null => {
    const trimmedValue = value.trim();
    if (!trimmedValue && isOptional) return null;

    if (valueType === 'number' && Number.isNaN(Number(trimmedValue))) {
      return 'Must be a valid number.';
    }

    if (valueType === 'date' && Number.isNaN(Date.parse(trimmedValue))) {
      return 'Must be a valid date.';
    }

    if (
      valueType === 'boolean' &&
      !['true', 'false'].includes(trimmedValue.toLowerCase())
    ) {
      return 'Must be either true or false.';
    }

    if (valueType === 'email') {
      const pattern = emailRegisterOptions.pattern.value;
      if (!pattern.test(trimmedValue)) return 'Must be a valid email.';
    }

    if (valueType === 'url') {
      const pattern = linkRegisterOptions.pattern.value;
      if (!pattern.test(trimmedValue)) return 'Must be a valid URL.';
    }

    if (valueType === 'phone') {
      const validation = phoneRegisterOptions.validate(trimmedValue);
      if (typeof validation === 'string') return validation;
    }

    return null;
  };

  const validatePayload = (
    candidateConditions: {
      logic?: string;
      rules?: Array<{ field?: string; operator?: string; value?: string }>;
    },
    candidateActions: Array<{
      type?: string;
      params?: Array<{ key?: string; value?: string }>;
    }>,
  ) => {
    const errors: Record<string, string> = {};
    const rules = Array.isArray(candidateConditions?.rules)
      ? candidateConditions.rules
      : [];
    const actions = Array.isArray(candidateActions) ? candidateActions : [];
    const triggerEvent = rule.triggerEvent || '';

    if (!rule.name?.trim()) {
      errors.name = 'Rule name is required.';
    } else if (rule.name.length > RULE_NAME_MAX_LENGTH) {
      errors.name = `Rule name must be ${RULE_NAME_MAX_LENGTH} characters or less.`;
    }

    if (!rule.triggerEvent) {
      errors.triggerEvent = 'Trigger event is required.';
    }

    // Validate time trigger config
    if (isTimeTriggerEvent) {
      const triggerConfig = parsedTriggerConfig;
      const time = triggerConfig?.time;
      if (!time || typeof time !== 'string' || !isValidTimeString(time)) {
        errors['triggerConfig.time'] = 'Please select a valid time.';
      }
      const offset = triggerConfig?.offset;
      if (
        typeof offset !== 'number' ||
        offset < -TIME_TRIGGER_OFFSET_MAX ||
        offset > TIME_TRIGGER_OFFSET_MAX
      ) {
        errors[
          'triggerConfig.offset'
        ] = `Offset must be between -${TIME_TRIGGER_OFFSET_MAX} and +${TIME_TRIGGER_OFFSET_MAX} days.`;
      }
    }

    if (TAG_TRIGGER_EVENTS.includes(triggerEvent) && rules.length === 0) {
      errors['condition-0-value'] = 'Select at least one tag.';
    }

    if (SERVICE_TRIGGER_EVENTS.includes(triggerEvent) && rules.length === 0) {
      errors['condition-0-value'] = 'Select at least one service.';
    }

    rules.forEach((row, index) => {
      // For non-tag/service triggers, incomplete/empty rows are allowed (no conditions = run always).
      const isIncomplete = !row.field || !(row.value ?? '').trim();
      if (
        isIncomplete &&
        !TAG_TRIGGER_EVENTS.includes(triggerEvent) &&
        !SERVICE_TRIGGER_EVENTS.includes(triggerEvent)
      ) {
        return;
      }
      if (!row.field) {
        errors[`condition-${index}-field`] = 'Select a field.';
      }
      if (!row.operator) {
        errors[`condition-${index}-operator`] = 'Select an operator.';
      }
      if (!row.value?.trim()) {
        errors[`condition-${index}-value`] = 'Enter a value.';
        return;
      }

      const fieldMeta = getFieldMeta(triggerEvent, row.field || '');
      if (!fieldMeta) return;

      if (fieldMeta.valueType === 'enum' && fieldMeta.options?.length) {
        const allowed = new Set(
          fieldMeta.options.map((option) => option.value),
        );
        if (!allowed.has(row.value)) {
          errors[`condition-${index}-value`] = 'Select a valid option.';
          return;
        }
      }

      const typedError = validateTypedValue(row.value, fieldMeta.valueType);
      if (typedError) {
        errors[`condition-${index}-value`] = typedError;
      }
    });

    if (!actions.length) {
      errors.actions = 'At least one action is required.';
    }
    if (actions.length > MAX_ACTIONS) {
      errors.actions = `Maximum ${MAX_ACTIONS} actions allowed.`;
    }

    const assignTagIds = actions
      .filter((a) => a.type === 'assign_tag')
      .flatMap((a) => {
        const p = (a.params || []).reduce((acc, param) => {
          if (param.key) acc[param.key] = param.value || '';
          return acc;
        }, {} as Record<string, string>);
        const raw = p.tag_ids || p.tag_id || '';
        return raw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      });
    const assignTagDupes = assignTagIds.filter(
      (id, i) => assignTagIds.indexOf(id) !== i,
    );
    if (assignTagDupes.length > 0) {
      const firstDupIndex = actions.findIndex(
        (a) =>
          a.type === 'assign_tag' &&
          (() => {
            const p = (a.params || []).reduce((acc, param) => {
              if (param.key) acc[param.key] = param.value || '';
              return acc;
            }, {} as Record<string, string>);
            const raw = p.tag_ids || p.tag_id || '';
            return raw
              .split(',')
              .map((s) => s.trim())
              .some((id) => assignTagDupes.includes(id));
          })(),
      );
      if (firstDupIndex !== -1) {
        errors[`action-${firstDupIndex}-param-tag_ids`] =
          'This tag is already used in another action.';
      }
    }

    const sendTemplateIds = actions
      .filter((a) => a.type === 'send_template')
      .map((a) => {
        const p = (a.params || []).reduce((acc, param) => {
          if (param.key) acc[param.key] = param.value || '';
          return acc;
        }, {} as Record<string, string>);
        return (p.follow_up_template_id || '').trim();
      })
      .filter(Boolean);
    const sendTemplateDupes = sendTemplateIds.filter(
      (id, i) => sendTemplateIds.indexOf(id) !== i,
    );
    if (sendTemplateDupes.length > 0) {
      const firstDupIndex = actions.findIndex(
        (a) =>
          a.type === 'send_template' &&
          (() => {
            const p = (a.params || []).reduce((acc, param) => {
              if (param.key) acc[param.key] = param.value || '';
              return acc;
            }, {} as Record<string, string>);
            return sendTemplateDupes.includes(
              (p.follow_up_template_id || '').trim(),
            );
          })(),
      );
      if (firstDupIndex !== -1) {
        errors[`action-${firstDupIndex}-param-follow_up_template_id`] =
          'This template is already used in another action.';
      }
    }

    actions.forEach((action, actionIndex) => {
      if (!action.type) {
        errors[`action-${actionIndex}-type`] = 'Select an action type.';
        return;
      }

      const schema = ACTION_SCHEMA_BY_TYPE[action.type];
      if (!schema) {
        errors[`action-${actionIndex}-type`] = 'Use a supported action type.';
        return;
      }

      const paramsByKey = (action.params || []).reduce((acc, param) => {
        if (param.key) acc[param.key] = param.value || '';
        return acc;
      }, {} as Record<string, string>);

      schema.params.forEach((param) => {
        const rawValue = (paramsByKey[param.key] || '').trim();
        if (param.required && !rawValue) {
          errors[
            `action-${actionIndex}-param-${param.key}`
          ] = `${param.label} is required.`;
          return;
        }

        if (!rawValue && !param.required) return;

        let valueType = 'string';
        if (param.control === 'email') valueType = 'email';
        if (param.control === 'phone') valueType = 'phone';
        if (param.control === 'url') valueType = 'url';
        if (param.control === 'number') valueType = 'number';

        if (param.control === 'select' && param.options?.length) {
          const allowed = new Set(param.options.map((option) => option.value));
          if (!allowed.has(rawValue)) {
            errors[`action-${actionIndex}-param-${param.key}`] =
              'Select a valid option.';
            return;
          }
        }

        const typedError = validateTypedValue(
          rawValue,
          valueType,
          !param.required,
        );
        if (typedError) {
          errors[`action-${actionIndex}-param-${param.key}`] = typedError;
          return;
        }
      });
    });

    return errors;
  };

  const liveValidationMap = (() => {
    const payload = buildStructuredPayload();
    return validatePayload(payload.conditions, payload.actions);
  })();

  const liveValidationErrors = Object.values(liveValidationMap);
  const hasAnyValidationErrors =
    Object.keys(saveValidationErrors).length > 0 ||
    liveValidationErrors.length > 0;

  /** Only show a field error if user has submitted or blurred that field. */
  const shouldShowError = (key: string) =>
    submitAttempted || touchedFields.has(key);

  const scrollToFirstError = (validationMap: Record<string, string>) => {
    const firstErrorKey = Object.keys(validationMap)[0];
    if (!firstErrorKey) return;
    const element = document.getElementById(
      `automation-error-${firstErrorKey}`,
    );
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement
      ) {
        element.focus();
      }
    }
  };

  const getError = (key: string) => {
    const message = saveValidationErrors[key] || liveValidationMap[key];
    return message && shouldShowError(key) ? message : undefined;
  };
  const getFieldClassName = (key: string) =>
    `w-full rounded-md border bg-neutral-50 px-3 py-2 text-sm text-body focus:outline-none focus:ring-2 ${
      getError(key)
        ? 'border-red-125 focus:border-red-125 focus:ring-red-50'
        : 'border-neutral-75 focus:border-secondary-100 focus:ring-secondary-50'
    }`;

  const getCompactControlClassName = (key: string) =>
    `rounded-md border bg-neutral-50 pl-2 pr-8 py-1.5 text-xs text-body focus:outline-none focus:ring-2 ${
      getError(key)
        ? 'border-red-125 focus:border-red-125 focus:ring-red-50'
        : 'border-neutral-75 focus:border-secondary-100 focus:ring-secondary-50'
    }`;

  const handleSave = () => {
    const payload = buildStructuredPayload();

    if (!payload) return;

    const errors = validatePayload(payload.conditions, payload.actions);
    if (Object.keys(errors).length > 0) {
      setSubmitAttempted(true);
      setSaveValidationErrors(errors);
      scrollToFirstError(errors);
      return;
    }

    onSave({
      ...rule,
      name: rule.name.trim(),
      conditions: payload.conditions,
      actions: payload.actions,
      triggerConfig: rule.triggerConfig ?? null,
    });
    setSaveValidationErrors({});
  };

  const triggerEventOptions = TRIGGER_OPTIONS_FOR_BUILDER.some(
    (option) => option.value === rule.triggerEvent,
  )
    ? TRIGGER_OPTIONS_FOR_BUILDER
    : [
        { value: rule.triggerEvent, label: `${rule.triggerEvent} (legacy)` },
        ...TRIGGER_OPTIONS_FOR_BUILDER,
      ];

  const setConditionRow = (
    rowIndex: number,
    patch: Partial<{ field: string; operator: string; value: string }>,
  ) => {
    setConditionRows((previousRows) =>
      previousRows.map((row, index) =>
        index === rowIndex ? { ...row, ...patch } : row,
      ),
    );
  };

  const setActionParamValue = (
    actionIndex: number,
    key: string,
    value: string,
  ) => {
    setActionRows((previousRows) =>
      previousRows.map((row, index) =>
        index === actionIndex
          ? {
              ...row,
              params: {
                ...row.params,
                [key]: value,
              },
            }
          : row,
      ),
    );
  };

  const selectedTagConditionRefs = Array.from(
    new Set(
      conditionRows
        .flatMap((row) => parseTagIds(row.value))
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  );
  const selectedTagIdForTrigger =
    selectedTagConditionRefs.length > 0 ? selectedTagConditionRefs[0] : '';

  const setTimeTriggerTagIds = (tagIds: string[]) => {
    const rules = normalizeTimeTriggerConditionRows(
      tagIds.filter(Boolean).map((tagId) => ({
        field: 'client_tag_ids',
        operator: 'contains',
        value: tagId,
      })),
    );
    setConditionRows(rules);
    updateRule({
      conditions: { logic: 'AND', rules },
    });
  };

  const selectedTimeTriggerTags = selectedTagConditionRefs.map((tagId) => {
    const tag = programTags.find((t) => t.id === tagId);
    return {
      id: tagId,
      name: tag?.name ?? tagId,
      color: tag?.color ?? DEFAULT_TAG_COLOR,
    };
  });
  const selectedTimeTriggerTagNames = formatTagNameList(
    selectedTimeTriggerTags.map((tag) => tag.name),
  );

  const selectedServiceIdForTrigger =
    conditionRows.find((r) => r.field === 'provider_service_id')?.value ?? '';
  const selectedPractitionerIdForTrigger =
    conditionRows.find((r) => r.field === 'assigned_provider_user_id')?.value ??
    '';
  const selectedPatientIdForTrigger =
    conditionRows.find((r) => r.field === 'patient_id')?.value ?? '';

  const renderTagOval = (tag: {
    id: string;
    name: string;
    color?: string | null;
  }) => {
    const styles = getTagStyles(tag.color ?? DEFAULT_TAG_COLOR);
    return (
      <div
        className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-small-caption"
        style={styles}
      >
        <Tag className="h-4 w-4" aria-hidden />
        <span className="text-sm">{tag.name}</span>
      </div>
    );
  };

  /** Single-tag dropdown: portal-rendered for modal compatibility, onMouseDown for reliable selection. */
  function TagSelectDropdown({
    value: tagId,
    onChange,
    onTagCreated,
    id,
    validationKey,
    errorMessage,
    hasError,
    onBlur,
    excludeTagIds = [],
    alwaysExcludeTagIds = [],
    label = 'Which tag?',
    allowEmpty = false,
    emptyButtonLabel = 'Search or create tag',
  }: {
    value: string;
    onChange: (tagId: string) => void;
    onTagCreated?: (tag: {
      id: string;
      name: string;
      color?: string | null;
    }) => void;
    id: string;
    validationKey?: string;
    errorMessage?: string;
    hasError?: boolean;
    onBlur?: () => void;
    /** Tag IDs to hide from the list (e.g. already selected in another action). Current value is always shown. */
    excludeTagIds?: string[];
    /** Tag IDs always hidden from the list (e.g. tag selected in trigger condition). No exception for current value. */
    alwaysExcludeTagIds?: string[];
    label?: string;
    allowEmpty?: boolean;
    emptyButtonLabel?: string;
  }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [dropdownRect, setDropdownRect] = useState<DOMRect | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const primaryId = (tagId || '').split(',')[0]?.trim() || '';
    const selectedTag = primaryId
      ? programTags.find((t) => t.id === primaryId)
      : null;
    const excludeSet = new Set(excludeTagIds);
    const alwaysExcludeSet = new Set(alwaysExcludeTagIds);
    const availableTags = programTags.filter(
      (t) =>
        !alwaysExcludeSet.has(t.id) &&
        (!excludeSet.has(t.id) || t.id === primaryId),
    );
    const filteredTags = query.trim()
      ? availableTags.filter((t) =>
          t.name.toLowerCase().includes(query.toLowerCase()),
        )
      : availableTags;
    const exactMatch = query.trim()
      ? availableTags.find(
          (t) => t.name.toLowerCase() === query.trim().toLowerCase(),
        )
      : null;
    const showCreateOption = query.trim().length > 0 && !exactMatch;

    useLayoutEffect(() => {
      if (open && buttonRef.current) {
        setDropdownRect(buttonRef.current.getBoundingClientRect());
      } else {
        setDropdownRect(null);
      }
    }, [open]);

    useEffect(() => {
      if (!open) return;

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        if (
          buttonRef.current?.contains(target) ||
          dropdownRef.current?.contains(target)
        ) {
          return;
        }
        setOpen(false);
      };

      const handleScroll = () => {
        if (buttonRef.current) {
          setDropdownRect(buttonRef.current.getBoundingClientRect());
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }, [open]);

    const handleSelectTag = (tagIdToSelect: string) => {
      onChange(tagIdToSelect);
      setQuery('');
      setOpen(false);
    };

    const handleCreateTag = async () => {
      const tagName = query.trim();
      if (!tagName) return;
      const key = validationKey ?? id.replace(/^automation-error-/, '');
      try {
        const { data } = await createProgramTagMutation({
          variables: {
            input: { name: tagName, color: DEFAULT_TAG_COLOR },
          },
          update: (cache, { data: mutationData }) => {
            const created = mutationData?.createProgramTag;
            if (!created?.id) return;
            cache.updateQuery(
              { query: MeProviderProgramTagsDocument },
              (existing) => {
                if (!existing?.meProvider?.provider?.programTags)
                  return existing;
                const tags = existing.meProvider.provider.programTags;
                if (tags.some((t) => t.id === created.id)) return existing;
                return {
                  ...existing,
                  meProvider: {
                    ...existing.meProvider,
                    provider: {
                      ...existing.meProvider.provider,
                      programTags: [
                        ...tags,
                        {
                          id: created.id,
                          name: created.name,
                          description: created.description,
                          color: created.color,
                        },
                      ],
                    },
                  },
                };
              },
            );
          },
        });
        const createdTag = data?.createProgramTag;
        if (createdTag?.id) {
          onTagCreated?.({
            id: createdTag.id,
            name: createdTag.name,
            color: createdTag.color,
          });
          setSaveValidationErrors((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
          onChange(createdTag.id);
          setQuery('');
          setOpen(false);
        }
      } catch (error) {
        // Log error for monitoring but show generic message to user
        logger.error('Failed to create program tag in automation editor', {
          tagName: query.trim(),
          error: error instanceof Error ? error : String(error),
        });
        setSaveValidationErrors((prev) => ({
          ...prev,
          [key]: 'Could not create tag. Please try again.',
        }));
      }
    };

    const dropdownContent = open && dropdownRect && (
      <Portal>
        <div
          ref={dropdownRef}
          className="overflow-hidden rounded-md border border-neutral-100 bg-white shadow-lg"
          style={{
            position: 'fixed',
            top: dropdownRect.bottom + 4,
            left: dropdownRect.left,
            width: dropdownRect.width,
            zIndex: 10000,
          }}
        >
          <div className="border-b border-neutral-100 bg-white px-2 py-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tags..."
              className="text-sm w-full rounded border border-neutral-100 px-3 py-2 placeholder:text-neutral-110 focus:border-primary-125 focus:outline-none focus:ring-1 focus:ring-primary-125"
            />
          </div>
          <div className="max-h-48 overflow-auto py-1">
            {allowEmpty && !query.trim() && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange('');
                  setQuery('');
                  setOpen(false);
                }}
                className={classNames(
                  'flex w-full cursor-pointer items-center gap-2 px-3 py-2 font-normal text-neutral-150 hover:bg-primary-25',
                  !primaryId && 'bg-primary-25',
                )}
              >
                {emptyButtonLabel}
              </button>
            )}
            {filteredTags.length === 0 &&
            !showCreateOption &&
            !(allowEmpty && !query.trim()) ? (
              <div className="text-sm text-neutral-130 px-3 py-2">
                {query.trim() ? 'No tags found' : 'Type to search or create'}
              </div>
            ) : (
              <>
                {showCreateOption && (
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCreateTag();
                    }}
                    className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 font-normal text-secondary-125 hover:bg-primary-25"
                  >
                    <PlusIcon className="h-5 w-5 shrink-0" aria-hidden />
                    <span>Create &quot;{query.trim()}&quot;</span>
                  </button>
                )}
                {filteredTags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectTag(tag.id);
                    }}
                    className={classNames(
                      'flex w-full cursor-pointer items-center gap-2 px-3 py-2 font-normal text-neutral-150 hover:bg-primary-25',
                      primaryId === tag.id && 'bg-primary-25',
                    )}
                  >
                    {renderTagOval(tag)}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      </Portal>
    );

    return (
      <div className="relative space-y-1">
        {label ? (
          <label
            className="text-xs text-neutral-130 block font-semibold"
            htmlFor={id}
          >
            {label}
          </label>
        ) : null}
        <button
          ref={buttonRef}
          type="button"
          id={id}
          onBlur={(e) => {
            if (
              e.relatedTarget &&
              dropdownRef.current?.contains(e.relatedTarget as Node)
            ) {
              return;
            }
            onBlur?.();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            setOpen((o) => !o);
          }}
          className={classNames(
            'flex w-full items-center gap-2 rounded-md border bg-white py-2 pl-3 pr-2 shadow-sm',
            hasError ? 'border-red-300' : 'border-neutral-100',
          )}
        >
          {selectedTag ? (
            renderTagOval(selectedTag)
          ) : (
            <>
              <TagIcon
                className="h-5 w-5 shrink-0 text-secondary-125"
                aria-hidden
              />
              <span className="text-sm min-w-0 flex-1 truncate text-left font-normal text-neutral-150">
                {emptyButtonLabel}
              </span>
            </>
          )}
          <ChevronDownIcon
            className="h-5 w-5 shrink-0 text-neutral-110"
            aria-hidden
          />
        </button>
        {dropdownContent}
        {errorMessage && (
          <ErrorMessage className="mt-1">{errorMessage}</ErrorMessage>
        )}
      </div>
    );
  }

  /** Service dropdown: portal-rendered for modal compatibility, onMouseDown for reliable selection. */
  function ServiceSelectDropdown({
    value: serviceId,
    onChange,
    id,
    validationKey,
    errorMessage,
    hasError,
    onBlur,
  }: {
    value: string;
    onChange: (serviceId: string) => void;
    id: string;
    validationKey?: string;
    errorMessage?: string;
    hasError?: boolean;
    onBlur?: () => void;
  }) {
    const [open, setOpen] = useState(false);
    const [dropdownRect, setDropdownRect] = useState<DOMRect | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const selectedService = serviceId
      ? providerServices.find((s) => s.id === serviceId)
      : null;
    const displayName = selectedService?.name || 'Select a service';

    useLayoutEffect(() => {
      if (open && buttonRef.current) {
        setDropdownRect(buttonRef.current.getBoundingClientRect());
      } else {
        setDropdownRect(null);
      }
    }, [open]);

    useEffect(() => {
      if (!open) return;

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        if (
          buttonRef.current?.contains(target) ||
          dropdownRef.current?.contains(target)
        ) {
          return;
        }
        setOpen(false);
      };

      const handleScroll = () => {
        if (buttonRef.current) {
          setDropdownRect(buttonRef.current.getBoundingClientRect());
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }, [open]);

    const dropdownContent = open && dropdownRect && (
      <Portal>
        <div
          ref={dropdownRef}
          className="max-h-60 list-none overflow-auto rounded-md border border-neutral-100 bg-white py-2 shadow-lg"
          style={{
            position: 'fixed',
            top: dropdownRect.bottom + 4,
            left: dropdownRect.left,
            width: dropdownRect.width,
            zIndex: 10000,
          }}
        >
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              onChange('');
              setOpen(false);
            }}
            className="text-neutral-130 flex w-full cursor-default items-center gap-2 px-3 py-2 text-left font-normal hover:bg-primary-25"
          >
            Select a service
          </button>
          {providerServices.map((service) => (
            <button
              key={service.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(service.id);
                setOpen(false);
              }}
              className="flex w-full cursor-default items-center gap-2 px-3 py-2 text-left font-normal text-neutral-150 hover:bg-primary-25"
            >
              {service.name ?? 'Untitled'}
            </button>
          ))}
        </div>
      </Portal>
    );

    return (
      <div className="relative space-y-1">
        <label
          className="text-xs text-neutral-130 block font-semibold"
          htmlFor={id}
        >
          Which service?
        </label>
        <div className="relative w-full min-w-0">
          <button
            ref={buttonRef}
            type="button"
            id={id}
            onMouseDown={(e) => {
              e.preventDefault();
              setOpen((prev) => !prev);
            }}
            onBlur={onBlur}
            className={classNames(
              'text-sm flex w-full items-center gap-2 rounded-md border bg-white py-2 pl-3 pr-2 text-left text-neutral-150 shadow-sm focus:outline-none focus:ring-2 focus:ring-secondary-100 focus:ring-offset-1',
              hasError ? 'border-red-300' : 'border-neutral-100',
            )}
          >
            <CalendarIcon
              className="h-5 w-5 shrink-0 text-secondary-125"
              aria-hidden
            />
            <span className="min-w-0 flex-1 truncate font-normal">
              {displayName}
            </span>
            <ChevronDownIcon
              className="h-5 w-5 shrink-0 text-neutral-110"
              aria-hidden
            />
          </button>
          {dropdownContent}
        </div>
        {errorMessage && (
          <ErrorMessage className="mt-1">{errorMessage}</ErrorMessage>
        )}
      </div>
    );
  }

  /** Practitioner dropdown: portal-rendered for modal compatibility, onMouseDown for reliable selection. */
  function PractitionerSelectDropdown({
    value: practitionerId,
    onChange,
    id,
    onBlur,
  }: {
    value: string;
    onChange: (practitionerId: string) => void;
    id: string;
    onBlur?: () => void;
  }) {
    const [open, setOpen] = useState(false);
    const [dropdownRect, setDropdownRect] = useState<DOMRect | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const selectedPractitioner = practitionerId
      ? providerUsers.find((u) => u.id === practitionerId)
      : null;
    const displayName =
      selectedPractitioner?.name ||
      selectedPractitioner?.email ||
      'Any practitioner';

    useLayoutEffect(() => {
      if (open && buttonRef.current) {
        setDropdownRect(buttonRef.current.getBoundingClientRect());
      } else {
        setDropdownRect(null);
      }
    }, [open]);

    useEffect(() => {
      if (!open) return;

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        if (
          buttonRef.current?.contains(target) ||
          dropdownRef.current?.contains(target)
        ) {
          return;
        }
        setOpen(false);
      };

      const handleScroll = () => {
        if (buttonRef.current) {
          setDropdownRect(buttonRef.current.getBoundingClientRect());
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }, [open]);

    const dropdownContent = open && dropdownRect && (
      <Portal>
        <div
          ref={dropdownRef}
          className="max-h-60 list-none overflow-auto rounded-md border border-neutral-100 bg-white py-2 shadow-lg"
          style={{
            position: 'fixed',
            top: dropdownRect.bottom + 4,
            left: dropdownRect.left,
            width: dropdownRect.width,
            zIndex: 10000,
          }}
        >
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              onChange('');
              setOpen(false);
            }}
            className="text-neutral-130 flex w-full cursor-default items-center gap-2 px-3 py-2 text-left font-normal hover:bg-primary-25"
          >
            Any practitioner
          </button>
          {providerUsers.map((user) => (
            <button
              key={user.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(user.id);
                setOpen(false);
              }}
              className="flex w-full cursor-default items-center gap-2 px-3 py-2 text-left font-normal text-neutral-150 hover:bg-primary-25"
            >
              {user.name || user.email || 'Untitled'}
            </button>
          ))}
        </div>
      </Portal>
    );

    return (
      <div className="relative space-y-1">
        <label
          className="text-xs text-neutral-130 block font-semibold"
          htmlFor={id}
        >
          Which practitioner?
        </label>
        <div className="relative w-full min-w-0">
          <button
            ref={buttonRef}
            type="button"
            id={id}
            onMouseDown={(e) => {
              e.preventDefault();
              setOpen((prev) => !prev);
            }}
            onBlur={onBlur}
            className="text-sm flex w-full items-center gap-2 rounded-md border border-neutral-100 bg-white py-2 pl-3 pr-2 text-left text-neutral-150 shadow-sm focus:outline-none focus:ring-2 focus:ring-secondary-100 focus:ring-offset-1"
          >
            <UserGroupIcon
              className="h-5 w-5 shrink-0 text-secondary-125"
              aria-hidden
            />
            <span className="min-w-0 flex-1 truncate font-normal">
              {displayName}
            </span>
            <ChevronDownIcon
              className="h-5 w-5 shrink-0 text-neutral-110"
              aria-hidden
            />
          </button>
          {dropdownContent}
        </div>
      </div>
    );
  }

  /** Client dropdown: portal-rendered for modal compatibility, onMouseDown for reliable selection. */
  function ClientSelectDropdown({
    value: patientId,
    onChange,
    id,
    onBlur,
  }: {
    value: string;
    onChange: (patientId: string) => void;
    id: string;
    onBlur?: () => void;
  }) {
    const [open, setOpen] = useState(false);
    const [dropdownRect, setDropdownRect] = useState<DOMRect | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const selectedPatient = patientId
      ? patients.find(
          (p) => p.id === patientId || p.programInstanceId === patientId,
        )
      : null;
    const displayName =
      selectedPatient?.name ||
      (selectedPatient?.firstName || selectedPatient?.lastName
        ? [selectedPatient.firstName, selectedPatient.lastName]
            .filter(Boolean)
            .join(' ')
        : null) ||
      selectedPatient?.email ||
      'Any client';

    useLayoutEffect(() => {
      if (open && buttonRef.current) {
        setDropdownRect(buttonRef.current.getBoundingClientRect());
      } else {
        setDropdownRect(null);
      }
    }, [open]);

    useEffect(() => {
      if (!open) return;

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        if (
          buttonRef.current?.contains(target) ||
          dropdownRef.current?.contains(target)
        ) {
          return;
        }
        setOpen(false);
      };

      const handleScroll = () => {
        if (buttonRef.current) {
          setDropdownRect(buttonRef.current.getBoundingClientRect());
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }, [open]);

    const dropdownContent = open && dropdownRect && (
      <Portal>
        <div
          ref={dropdownRef}
          className="max-h-60 list-none overflow-auto rounded-md border border-neutral-100 bg-white py-2 shadow-lg"
          style={{
            position: 'fixed',
            top: dropdownRect.bottom + 4,
            left: dropdownRect.left,
            width: dropdownRect.width,
            zIndex: 10000,
          }}
        >
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              onChange('');
              setOpen(false);
            }}
            className="text-neutral-130 flex w-full cursor-default items-center gap-2 px-3 py-2 text-left font-normal hover:bg-primary-25"
          >
            Any client
          </button>
          {patients.map((patient) => {
            const pId = patient.id ?? patient.programInstanceId;
            if (!pId) return null;
            const pName =
              patient.name ||
              [patient.firstName, patient.lastName].filter(Boolean).join(' ') ||
              patient.email ||
              'Untitled';
            return (
              <button
                key={pId}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(pId);
                  setOpen(false);
                }}
                className="flex w-full cursor-default items-center gap-2 px-3 py-2 text-left font-normal text-neutral-150 hover:bg-primary-25"
              >
                {pName}
              </button>
            );
          })}
        </div>
      </Portal>
    );

    return (
      <div className="relative space-y-1">
        <label
          className="text-xs text-neutral-130 block font-semibold"
          htmlFor={id}
        >
          Which client?
        </label>
        <div className="relative w-full min-w-0">
          <button
            ref={buttonRef}
            type="button"
            id={id}
            onMouseDown={(e) => {
              e.preventDefault();
              setOpen((prev) => !prev);
            }}
            onBlur={onBlur}
            className="text-sm flex w-full items-center gap-2 rounded-md border border-neutral-100 bg-white py-2 pl-3 pr-2 text-left text-neutral-150 shadow-sm focus:outline-none focus:ring-2 focus:ring-secondary-100 focus:ring-offset-1"
          >
            <UserIcon
              className="h-5 w-5 shrink-0 text-secondary-125"
              aria-hidden
            />
            <span className="min-w-0 flex-1 truncate font-normal">
              {displayName}
            </span>
            <ChevronDownIcon
              className="h-5 w-5 shrink-0 text-neutral-110"
              aria-hidden
            />
          </button>
          {dropdownContent}
        </div>
      </div>
    );
  }

  const isPreviewComplete = (() => {
    if (!rule) return false;
    const actionableRows = actionRows.filter(
      (a) => a.type && a.type !== 'call_webhook',
    );
    if (actionableRows.length === 0) return false;
    for (const action of actionableRows) {
      const schema = ACTION_SCHEMA_BY_TYPE[action.type];
      if (schema?.params) {
        const requiredParams = schema.params.filter((p) => p.required);
        for (const param of requiredParams) {
          const val = action.params[param.key];
          if (!val || !String(val).trim()) return false;
        }
      }
    }
    if (isTagTriggerEvent) {
      return selectedTagConditionRefs.length > 0;
    }
    if (isServiceTriggerEvent) {
      return Boolean(selectedServiceIdForTrigger?.trim());
    }
    return true;
  })();

  const previewRule: AutomationRulePartial | null =
    rule && isPreviewComplete
      ? {
          name: rule.name,
          triggerEvent: rule.triggerEvent,
          triggerConfig: rule.triggerConfig,
          conditions: {
            logic: 'AND' as const,
            rules: conditionRows,
          },
          actions: actionRows
            .filter((a) => a.type && a.type !== 'call_webhook')
            .map((a) => ({
              type: a.type!,
              params: Object.entries(a.params || {}).map(([key, value]) => ({
                key,
                value: String(value ?? ''),
              })),
            })),
        }
      : null;

  const isNewRule = Boolean(rule?.id?.startsWith('temp-'));

  return (
    <div className="w-full overflow-y-auto bg-white px-6 pb-6 pt-0">
      <div className="space-y-6">
        <div className="rounded-md border border-neutral-100 bg-neutral-25 p-4">
          <div>
            <label className="text-xs text-neutral-130 mb-2 block font-semibold">
              Name
            </label>
            <input
              type="text"
              value={rule.name || ''}
              onChange={(event) => updateRule({ name: event.target.value })}
              onBlur={registerBlur('name')}
              id="automation-error-name"
              placeholder="e.g., Welcome New Clients"
              maxLength={RULE_NAME_MAX_LENGTH}
              className={classNames(getFieldClassName('name'), '!bg-white')}
            />
            <ErrorMessage className="mt-1">{getError('name')}</ErrorMessage>
          </div>
        </div>

        <div className="space-y-2">
          <div className="rounded-lg border border-neutral-100 bg-neutral-25/50 p-6 transition-all hover:border-neutral-200">
            <div className="mb-4 flex items-center gap-3">
              <span
                className="text-sm flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-100 font-bold text-white shadow-sm"
                aria-hidden
              >
                1
              </span>
              <p className="text-base font-semibold text-neutral-150">
                When this happens...
              </p>
            </div>
            <div className="text-sm text-neutral-130 space-y-4 font-medium">
              <div className="w-full rounded-md border border-neutral-100 bg-white shadow-sm">
                <Listbox
                  value={rule.triggerEvent || ''}
                  onChange={(nextTrigger: string) => {
                    const patch: Partial<AutomationRulePartial> = {
                      triggerEvent:
                        nextTrigger as AutomationRulePartial['triggerEvent'],
                    };
                    if (isTimeBasedTrigger(nextTrigger)) {
                      // Initialize or preserve triggerConfig for time-based triggers
                      patch.triggerConfig =
                        rule.triggerConfig ??
                        JSON.stringify(DEFAULT_TIME_TRIGGER_CONFIG);
                    } else {
                      // Clear triggerConfig when switching away from time-based triggers
                      patch.triggerConfig = null;
                    }
                    const leavingTime = isTimeBasedTrigger(
                      rule.triggerEvent || '',
                    );
                    const enteringTime = isTimeBasedTrigger(nextTrigger);
                    if (
                      leavingTime !== enteringTime ||
                      nextTrigger === 'client_created' ||
                      nextTrigger === 'practitioner_assigned'
                    ) {
                      patch.conditions = { logic: 'AND', rules: [] };
                      setConditionRows([]);
                    }
                    updateRule(patch);
                  }}
                >
                  <div className="relative w-full min-w-0">
                    <Listbox.Button
                      id="automation-error-triggerEvent"
                      onBlur={registerBlur('triggerEvent')}
                      className={`flex w-full items-center gap-2 rounded-md bg-white px-3 py-2 text-left font-normal text-neutral-150 focus:outline-none focus:ring-2 focus:ring-secondary-100 focus:ring-offset-1 ${getFieldClassName(
                        'triggerEvent',
                      )}`}
                    >
                      {(() => {
                        const TriggerIcon =
                          TRIGGER_EVENT_ICONS[rule.triggerEvent || ''] ??
                          TagIcon;
                        return (
                          <TriggerIcon
                            className="h-5 w-5 shrink-0 text-secondary-125"
                            aria-hidden
                          />
                        );
                      })()}
                      <span className="min-w-0 flex-1 truncate">
                        {TRIGGER_OPTIONS.find(
                          (o) => o.value === rule.triggerEvent,
                        )?.label ??
                          triggerEventOptions.find(
                            (o) => o.value === rule.triggerEvent,
                          )?.label ??
                          rule.triggerEvent ??
                          'Select event'}
                      </span>
                      <ChevronDownIcon
                        className="text-neutral-130 h-4 w-4 shrink-0"
                        aria-hidden
                      />
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 w-full min-w-full list-none overflow-auto rounded-md border border-neutral-100 bg-white py-2 shadow-lg focus:outline-none">
                        {triggerEventOptions.map((option) => {
                          const Icon =
                            TRIGGER_EVENT_ICONS[option.value] ?? TagIcon;
                          return (
                            <Listbox.Option
                              key={option.value}
                              value={option.value}
                              className={({ active }) =>
                                classNames(
                                  'flex cursor-default items-center gap-2 px-3 py-2 font-normal text-neutral-150',
                                  active && 'bg-primary-25',
                                )
                              }
                            >
                              <Icon
                                className="h-5 w-5 shrink-0 text-secondary-125"
                                aria-hidden
                              />
                              <span>{option.label}</span>
                            </Listbox.Option>
                          );
                        })}
                        {TRIGGER_OPTIONS_COMING_SOON.length > 0 && (
                          <>
                            <div
                              className="border-t border-neutral-100 px-3 py-1.5 text-[11px] font-medium text-neutral-110"
                              role="separator"
                            >
                              Coming soon
                            </div>
                            {TRIGGER_OPTIONS_COMING_SOON.map((option) => {
                              const Icon =
                                TRIGGER_EVENT_ICONS[option.value] ?? TagIcon;
                              return (
                                <Listbox.Option
                                  key={option.value}
                                  value={option.value}
                                  disabled
                                  className="flex cursor-not-allowed items-center gap-2 px-3 py-2 font-normal text-neutral-110 opacity-75"
                                >
                                  <Icon
                                    className="h-5 w-5 shrink-0 text-neutral-110"
                                    aria-hidden
                                  />
                                  <span>{option.label}</span>
                                  <span className="text-neutral-120 ml-auto rounded bg-neutral-75 px-1.5 py-0.5 text-[10px] font-medium">
                                    Coming soon
                                  </span>
                                </Listbox.Option>
                              );
                            })}
                          </>
                        )}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
              </div>
              {getError('triggerEvent') && (
                <ErrorMessage className="mt-1">
                  {getError('triggerEvent')}
                </ErrorMessage>
              )}
              {isPractitionerTriggerEvent && (
                <div className="mt-4 space-y-4 overflow-visible">
                  <PractitionerSelectDropdown
                    value={selectedPractitionerIdForTrigger}
                    onChange={(practitionerId) => {
                      const newRules: Array<{
                        field: string;
                        operator: string;
                        value: string;
                      }> = [];
                      if (practitionerId) {
                        newRules.push({
                          field: 'assigned_provider_user_id',
                          operator: 'eq',
                          value: practitionerId,
                        });
                      }
                      const patientRule = conditionRows.find(
                        (r) =>
                          r.field === 'patient_id' && (r.value ?? '').trim(),
                      );
                      if (patientRule) {
                        newRules.push(patientRule);
                      }
                      setConditionRows(newRules);
                      updateRule({
                        conditions: {
                          logic: 'AND',
                          rules: newRules,
                        },
                      });
                    }}
                    onBlur={registerBlur('condition-practitioner')}
                    id="automation-error-condition-practitioner"
                  />
                  <ClientSelectDropdown
                    value={selectedPatientIdForTrigger}
                    onChange={(patientId) => {
                      const newRules: Array<{
                        field: string;
                        operator: string;
                        value: string;
                      }> = [];
                      const practitionerRule = conditionRows.find(
                        (r) =>
                          r.field === 'assigned_provider_user_id' &&
                          (r.value ?? '').trim(),
                      );
                      if (practitionerRule) {
                        newRules.push(practitionerRule);
                      }
                      if (patientId) {
                        newRules.push({
                          field: 'patient_id',
                          operator: 'eq',
                          value: patientId,
                        });
                      }
                      setConditionRows(newRules);
                      updateRule({
                        conditions: {
                          logic: 'AND',
                          rules: newRules,
                        },
                      });
                    }}
                    onBlur={registerBlur('condition-patient')}
                    id="automation-error-condition-patient"
                  />
                </div>
              )}
              {isServiceTriggerEvent && (
                <div className="mt-4">
                  <ServiceSelectDropdown
                    value={selectedServiceIdForTrigger}
                    onChange={(serviceId) => {
                      if (!serviceId) {
                        setConditionRows([]);
                        updateRule({
                          conditions: { logic: 'AND', rules: [] },
                        });
                        setSaveValidationErrors((prev) => {
                          const next = { ...prev };
                          delete next['condition-0-value'];
                          return next;
                        });
                        return;
                      }
                      const newCondition = {
                        field: 'provider_service_id',
                        operator: 'contains' as const,
                        value: serviceId,
                      };
                      setConditionRows([newCondition]);
                      updateRule({
                        conditions: {
                          logic: 'AND',
                          rules: [newCondition],
                        },
                      });
                      setSaveValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next['condition-0-value'];
                        return next;
                      });
                    }}
                    onBlur={registerBlur('condition-0-value')}
                    id="automation-error-condition-0-value-trigger"
                    validationKey="condition-0-value"
                    errorMessage={getError('condition-0-value') ?? undefined}
                    hasError={Boolean(getError('condition-0-value'))}
                  />
                </div>
              )}
              {isTagTriggerEvent && (
                <div className="mt-4">
                  <TagSelectDropdown
                    value={selectedTagIdForTrigger}
                    onTagCreated={(tag) => {
                      setNewlyCreatedTags((prev) => ({
                        ...prev,
                        [tag.id]: tag,
                      }));
                      onTagCreatedFromParent?.(tag);
                    }}
                    onChange={(tagId) => {
                      if (!tagId) {
                        setConditionRows([]);
                        updateRule({
                          conditions: { logic: 'AND', rules: [] },
                        });
                        setSaveValidationErrors((prev) => {
                          const next = { ...prev };
                          delete next['condition-0-value'];
                          return next;
                        });
                        return;
                      }
                      const newCondition = {
                        field: 'client_tag_ids',
                        operator: 'contains' as const,
                        value: tagId,
                      };
                      setConditionRows([newCondition]);
                      updateRule({
                        conditions: {
                          logic: 'AND',
                          rules: [newCondition],
                        },
                      });
                      setSaveValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next['condition-0-value'];
                        return next;
                      });
                      setActionRows((prev) => {
                        const cleared: number[] = [];
                        const next = prev.map((row, idx) => {
                          if (row.type !== 'assign_tag') return row;
                          const ids = (
                            row.params.tag_ids ||
                            row.params.tag_id ||
                            ''
                          )
                            .split(',')
                            .map((s) => s.trim())
                            .filter(Boolean);
                          if (ids.includes(tagId)) {
                            cleared.push(idx);
                            return {
                              ...row,
                              params: {
                                ...row.params,
                                tag_ids: '',
                                tag_id: '',
                              },
                            };
                          }
                          return row;
                        });
                        if (cleared.length > 0) {
                          setSaveValidationErrors((p) => {
                            const n = { ...p };
                            cleared.forEach((i) => {
                              delete n[`action-${i}-param-tag_ids`];
                            });
                            return n;
                          });
                        }
                        return next;
                      });
                    }}
                    onBlur={registerBlur('condition-0-value')}
                    id="automation-error-condition-0-value-trigger"
                    validationKey="condition-0-value"
                    errorMessage={getError('condition-0-value') ?? undefined}
                    hasError={Boolean(getError('condition-0-value'))}
                  />
                </div>
              )}
              {isTimeTriggerEvent && (
                <div className="mt-4 rounded-lg border-2 border-neutral-100 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-2">
                    <ClockIcon className="text-neutral-120 h-5 w-5" />
                    <p className="text-sm text-neutral-120 font-normal">
                      {`This automation will trigger based on each client's program ${triggerAnchorLabel} date${
                        selectedTimeTriggerTagNames
                          ? ` for clients tagged with ${selectedTimeTriggerTagNames}`
                          : ''
                      }.`}
                    </p>
                  </div>
                  <div className="mt-3">
                    <OffsetControl
                      offset={effectiveTriggerConfig.offset}
                      onChange={(newOffset) => {
                        const currentConfig = effectiveTriggerConfig;
                        updateRule({
                          triggerConfig: JSON.stringify({
                            ...currentConfig,
                            offset: newOffset,
                          }),
                        });
                      }}
                      anchorLabel={triggerAnchorLabelLong}
                    />
                  </div>
                  {getError('triggerConfig.offset') && (
                    <ErrorMessage className="mt-1">
                      {getError('triggerConfig.offset')}
                    </ErrorMessage>
                  )}
                  <div className="mt-4 border-t border-neutral-100 pt-4">
                    <p className="text-xs font-semibold text-neutral-150">
                      More options
                    </p>
                    <p className="text-xs text-neutral-120 mt-0.5 mb-2">
                      Only trigger for clients who have all of the selected tags
                    </p>
                    {selectedTimeTriggerTags.length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-2">
                        {selectedTimeTriggerTags.map((tag) => {
                          const styles = getTagStyles(tag.color);
                          return (
                            <span
                              key={tag.id}
                              className="text-sm inline-flex items-center gap-1 rounded-full px-2 py-1"
                              style={styles}
                            >
                              {tag.name}
                              <button
                                type="button"
                                onClick={() =>
                                  setTimeTriggerTagIds(
                                    selectedTagConditionRefs.filter(
                                      (id) => id !== tag.id,
                                    ),
                                  )
                                }
                                className="rounded-full p-0.5 hover:opacity-80"
                                aria-label={`Remove ${tag.name}`}
                              >
                                <XIcon className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <TagSelectDropdown
                      value=""
                      allowEmpty={selectedTagConditionRefs.length === 0}
                      label=""
                      emptyButtonLabel={
                        selectedTagConditionRefs.length === 0
                          ? 'No tag selected'
                          : 'Add another tag'
                      }
                      excludeTagIds={selectedTagConditionRefs}
                      onTagCreated={(tag) => {
                        setNewlyCreatedTags((prev) => ({
                          ...prev,
                          [tag.id]: tag,
                        }));
                        onTagCreatedFromParent?.(tag);
                      }}
                      onChange={(tagId) => {
                        if (!tagId) {
                          setTimeTriggerTagIds([]);
                          return;
                        }
                        setTimeTriggerTagIds([
                          ...selectedTagConditionRefs,
                          tagId,
                        ]);
                      }}
                      id="automation-time-trigger-live-tag"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {showsGenericConditions && (
            <div className="flex justify-center py-0.5">
              <span className="text-2xl text-neutral-120" aria-hidden>
                ↓
              </span>
            </div>
          )}

          <div>
            <div className="space-y-2">
              {showsGenericConditions ? (
                <div className="rounded-lg border-2 border-neutral-100 bg-white p-4 shadow-sm">
                  <div>
                    <label className="text-xs text-neutral-130 mb-2 block font-semibold">
                      Conditions
                    </label>
                    <div className="space-y-3 rounded-md border border-neutral-100 p-3">
                      <div className="space-y-2">
                        {conditionRows.map((row, rowIndex) => (
                          <div
                            key={`condition-${rowIndex}`}
                            className="space-y-1"
                          >
                            <div className="grid grid-cols-12 items-center gap-2">
                              <select
                                value={row.field}
                                onChange={(event) =>
                                  setConditionRow(rowIndex, {
                                    field: event.target.value,
                                    operator: getDefaultOperator(
                                      rule.triggerEvent || '',
                                      event.target.value,
                                    ),
                                    value: '',
                                  })
                                }
                                onBlur={registerBlur(
                                  `condition-${rowIndex}-field`,
                                )}
                                id={`automation-error-condition-${rowIndex}-field`}
                                className={`col-span-3 ${getCompactControlClassName(
                                  `condition-${rowIndex}-field`,
                                )}`}
                              >
                                {getConditionFields(rule.triggerEvent || '')
                                  .length === 0 ? (
                                  <option value="">No fields available</option>
                                ) : (
                                  getConditionFields(
                                    rule.triggerEvent || '',
                                  ).map((fieldOption) => (
                                    <option
                                      key={fieldOption.key}
                                      value={fieldOption.key}
                                    >
                                      {fieldOption.label}
                                    </option>
                                  ))
                                )}
                              </select>
                              <select
                                value={row.operator}
                                onChange={(event) =>
                                  setConditionRow(rowIndex, {
                                    operator: event.target.value,
                                  })
                                }
                                onBlur={registerBlur(
                                  `condition-${rowIndex}-operator`,
                                )}
                                id={`automation-error-condition-${rowIndex}-operator`}
                                className={`col-span-2 ${getCompactControlClassName(
                                  `condition-${rowIndex}-operator`,
                                )}`}
                              >
                                {getOperatorOptions(
                                  rule.triggerEvent || '',
                                  row.field,
                                ).map((option) => (
                                  <option key={option} value={option}>
                                    {OPERATOR_LABELS[option] || option}
                                  </option>
                                ))}
                              </select>
                              {getFieldMeta(rule.triggerEvent || '', row.field)
                                ?.valueType === 'enum' ? (
                                <select
                                  value={row.value}
                                  onChange={(event) =>
                                    setConditionRow(rowIndex, {
                                      value: event.target.value,
                                    })
                                  }
                                  onBlur={registerBlur(
                                    `condition-${rowIndex}-value`,
                                  )}
                                  id={`automation-error-condition-${rowIndex}-value`}
                                  className={`col-span-6 ${getCompactControlClassName(
                                    `condition-${rowIndex}-value`,
                                  )}`}
                                >
                                  {(
                                    getFieldMeta(
                                      rule.triggerEvent || '',
                                      row.field,
                                    )?.options || []
                                  ).map((option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  value={row.value}
                                  onChange={(event) =>
                                    setConditionRow(rowIndex, {
                                      value: event.target.value,
                                    })
                                  }
                                  onBlur={registerBlur(
                                    `condition-${rowIndex}-value`,
                                  )}
                                  type={
                                    getFieldMeta(
                                      rule.triggerEvent || '',
                                      row.field,
                                    )?.valueType === 'number'
                                      ? 'number'
                                      : getFieldMeta(
                                          rule.triggerEvent || '',
                                          row.field,
                                        )?.valueType === 'date'
                                      ? 'date'
                                      : 'text'
                                  }
                                  placeholder="value"
                                  id={`automation-error-condition-${rowIndex}-value`}
                                  className={`col-span-6 ${getCompactControlClassName(
                                    `condition-${rowIndex}-value`,
                                  )}`}
                                />
                              )}
                              <button
                                type="button"
                                onClick={() =>
                                  setConditionRows((prev) =>
                                    prev.filter(
                                      (_, index) => index !== rowIndex,
                                    ),
                                  )
                                }
                                className="text-red-500 hover:text-red-700 col-span-1"
                                title="Delete condition"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="grid grid-cols-12 gap-2">
                              <div className="col-span-3" />
                              <div className="col-span-2" />
                              <ErrorMessage className="col-span-6">
                                {getError(`condition-${rowIndex}-value`)}
                              </ErrorMessage>
                              <div className="col-span-1" />
                            </div>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() =>
                            setConditionRows((prev) => [
                              ...prev,
                              {
                                field: getDefaultConditionField(
                                  rule.triggerEvent || '',
                                ),
                                operator: getDefaultOperator(
                                  rule.triggerEvent || '',
                                  getDefaultConditionField(
                                    rule.triggerEvent || '',
                                  ),
                                ),
                                value: '',
                              },
                            ])
                          }
                          className="text-xs inline-flex items-center text-secondary-100 hover:underline"
                        >
                          <PlusIcon className="mr-1 h-3 w-3" />
                          Add condition
                        </button>
                        {conditionRows.length === 0 && (
                          <p className="text-xs text-neutral-120">
                            No conditions configured. This rule will run for
                            every{' '}
                            {TRIGGER_OPTIONS.find(
                              (option) => option.value === rule.triggerEvent,
                            )?.label?.toLowerCase() || 'trigger'}{' '}
                            event.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="flex justify-center py-0.5">
                <span className="text-2xl text-neutral-120" aria-hidden>
                  ↓
                </span>
              </div>

              <div className="rounded-lg border border-neutral-100 bg-neutral-25/50 p-6 transition-all hover:border-neutral-200">
                <div className="mb-4 flex items-center gap-3">
                  <span
                    className="text-sm flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-100 font-bold text-white shadow-sm"
                    aria-hidden
                  >
                    2
                  </span>
                  <div className="flex-1">
                    <p className="text-base font-semibold text-neutral-150">
                      {isTimeTriggerEvent ? 'Do this at...' : 'Do this...'}
                    </p>
                    {isTimeTriggerEvent && (
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          id="automation-trigger-config-time"
                          type="time"
                          className={getFieldClassName('triggerConfig.time')}
                          value={effectiveTriggerConfig.time}
                          onChange={(e) => {
                            const currentConfig = effectiveTriggerConfig;
                            updateRule({
                              triggerConfig: JSON.stringify({
                                ...currentConfig,
                                time:
                                  e.target.value ||
                                  DEFAULT_TIME_TRIGGER_CONFIG.time,
                              }),
                            });
                          }}
                          onBlur={registerBlur('triggerConfig.time')}
                        />
                        <span className="text-xs text-neutral-120">
                          {getTimezoneAbbreviation(
                            authedProviderUser?.provider?.timezone ||
                              DEFAULT_MAIL_TIMEZONE,
                          )}
                        </span>
                        {getError('triggerConfig.time') && (
                          <ErrorMessage className="text-sm mt-1">
                            {getError('triggerConfig.time')}
                          </ErrorMessage>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-4">
                    {actionRows.map((action, actionIndex) => (
                      <Fragment key={`action-${actionIndex}`}>
                        {actionIndex > 0 && (
                          <p className="text-sm text-neutral-120 text-center font-medium">
                            and
                          </p>
                        )}
                        <div className="rounded-md border border-neutral-100 bg-neutral-50 p-2">
                          <>
                            <div
                              className={classNames(
                                'mb-3 flex items-center gap-2 rounded-md border bg-white px-3 py-2 shadow-sm',
                                getError(`action-${actionIndex}-type`)
                                  ? 'border-red-300'
                                  : 'border-neutral-100',
                              )}
                            >
                              <div
                                ref={(el) => {
                                  if (el) {
                                    const measure = () =>
                                      el.style.setProperty(
                                        '--action-type-trigger-width',
                                        `${el.offsetWidth}px`,
                                      );
                                    measure();
                                    requestAnimationFrame(measure);
                                  }
                                }}
                                className="min-w-0 flex-1"
                              >
                                <Listbox
                                  value={action.type}
                                  onChange={(value: string) => {
                                    registerBlur(
                                      `action-${actionIndex}-type`,
                                    )();
                                    setActionRows((prev) =>
                                      prev.map((item, index) => {
                                        if (index !== actionIndex) return item;
                                        return {
                                          ...item,
                                          type: value,
                                          params: getDefaultActionParams(value),
                                        };
                                      }),
                                    );
                                  }}
                                >
                                  <div className="relative w-full min-w-0">
                                    <Listbox.Button
                                      id={`automation-error-action-${actionIndex}-type`}
                                      className="flex w-full cursor-default items-center gap-2 rounded border-0 bg-transparent p-0 text-left text-neutral-150 focus:ring-0"
                                    >
                                      {action.type &&
                                      ACTION_TYPE_ICONS[action.type]
                                        ? (() => {
                                            const Icon =
                                              ACTION_TYPE_ICONS[action.type];
                                            return (
                                              <Icon
                                                className="h-5 w-5 shrink-0 text-green-125"
                                                aria-hidden
                                              />
                                            );
                                          })()
                                        : null}
                                      <span className="min-w-0 flex-1 truncate">
                                        {action.type
                                          ? ACTION_SCHEMA_BY_TYPE[action.type]
                                              ?.label ?? action.type
                                          : 'Select action'}
                                      </span>
                                      <ChevronDownIcon
                                        className="text-neutral-130 h-4 w-4 shrink-0"
                                        aria-hidden
                                      />
                                    </Listbox.Button>
                                    <Transition
                                      as={Fragment}
                                      enter="transition ease-out duration-100"
                                      enterFrom="opacity-0"
                                      enterTo="opacity-100"
                                      leave="transition ease-in duration-75"
                                      leaveFrom="opacity-100"
                                      leaveTo="opacity-0"
                                    >
                                      <Listbox.Options
                                        className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 list-none overflow-auto rounded-md border border-neutral-100 bg-white py-2 shadow-lg focus:outline-none"
                                        style={{
                                          width:
                                            'var(--action-type-trigger-width)',
                                          minWidth:
                                            'var(--action-type-trigger-width)',
                                        }}
                                      >
                                        <Listbox.Option value="">
                                          {({ active }) => (
                                            <span
                                              className={classNames(
                                                'text-neutral-130 flex cursor-default items-center gap-2 px-3 py-2 font-normal',
                                                active && 'bg-primary-25',
                                              )}
                                            >
                                              Select action
                                            </span>
                                          )}
                                        </Listbox.Option>
                                        {ACTION_SCHEMAS_FOR_BUILDER.map(
                                          (option) => {
                                            const Icon =
                                              ACTION_TYPE_ICONS[option.type];
                                            const isCreateInvoice =
                                              option.type === 'create_invoice';
                                            const isDisabled =
                                              isCreateInvoice &&
                                              !hasStripeConnected;
                                            const optionContent = (
                                              <>
                                                {Icon ? (
                                                  <Icon
                                                    className="h-5 w-5 shrink-0 text-green-125"
                                                    aria-hidden
                                                  />
                                                ) : null}
                                                <span>{option.label}</span>
                                              </>
                                            );
                                            return (
                                              <Listbox.Option
                                                key={option.type}
                                                value={option.type}
                                                disabled={isDisabled}
                                                className={({ active }) =>
                                                  classNames(
                                                    'flex cursor-default items-center gap-2 px-3 py-2 font-normal text-neutral-150',
                                                    active &&
                                                      !isDisabled &&
                                                      'bg-primary-25',
                                                    isDisabled &&
                                                      'cursor-not-allowed opacity-50',
                                                  )
                                                }
                                              >
                                                {isDisabled ? (
                                                  <TooltipLight
                                                    content="Connect Stripe to create invoices"
                                                    placement="right"
                                                    className="text-neutral-130 border-neutral-100 bg-neutral-75 px-2.5 py-1.5 text-[12px] font-normal shadow-none"
                                                  >
                                                    <span className="pointer-events-auto flex w-full min-w-0 items-center gap-2">
                                                      {optionContent}
                                                    </span>
                                                  </TooltipLight>
                                                ) : (
                                                  optionContent
                                                )}
                                              </Listbox.Option>
                                            );
                                          },
                                        )}
                                        {ACTION_SCHEMAS_COMING_SOON.length >
                                          0 && (
                                          <>
                                            <div
                                              className="border-t border-neutral-100 px-3 py-1.5 text-[11px] font-medium text-neutral-110"
                                              role="separator"
                                            >
                                              Coming soon
                                            </div>
                                            {ACTION_SCHEMAS_COMING_SOON.map(
                                              (option) => {
                                                const Icon =
                                                  ACTION_TYPE_ICONS[
                                                    option.type
                                                  ];
                                                return (
                                                  <Listbox.Option
                                                    key={option.type}
                                                    value={option.type}
                                                    disabled
                                                    className="flex cursor-not-allowed items-center gap-2 px-3 py-2 font-normal text-neutral-110 opacity-75"
                                                  >
                                                    {Icon ? (
                                                      <Icon
                                                        className="h-5 w-5 shrink-0 text-neutral-110"
                                                        aria-hidden
                                                      />
                                                    ) : null}
                                                    <span>{option.label}</span>
                                                    <span className="text-neutral-120 ml-auto rounded bg-neutral-75 px-1.5 py-0.5 text-[10px] font-medium">
                                                      Coming soon
                                                    </span>
                                                  </Listbox.Option>
                                                );
                                              },
                                            )}
                                          </>
                                        )}
                                      </Listbox.Options>
                                    </Transition>
                                  </div>
                                </Listbox>
                              </div>
                              {actionRows.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setActionRows((prev) =>
                                      prev.filter(
                                        (_, index) => index !== actionIndex,
                                      ),
                                    )
                                  }
                                  className="text-red-500 hover:text-red-700 shrink-0"
                                  title="Delete action"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                            {action.type !== 'assign_tag' && (
                              <ErrorMessage className="mt-1">
                                {getError(`action-${actionIndex}-type`)}
                              </ErrorMessage>
                            )}
                          </>

                          {action.type === 'assign_tag' ? (
                            <div className="space-y-3">
                              <TagSelectDropdown
                                value={
                                  action.params.tag_ids ||
                                  action.params.tag_id ||
                                  ''
                                }
                                onTagCreated={(tag) => {
                                  setNewlyCreatedTags((prev) => ({
                                    ...prev,
                                    [tag.id]: tag,
                                  }));
                                  onTagCreatedFromParent?.(tag);
                                }}
                                onChange={(tagId) =>
                                  setActionParamValue(
                                    actionIndex,
                                    'tag_ids',
                                    tagId,
                                  )
                                }
                                onBlur={registerBlur(
                                  `action-${actionIndex}-param-tag_ids`,
                                )}
                                id={`automation-error-action-${actionIndex}-param-tag_ids`}
                                validationKey={`action-${actionIndex}-param-tag_ids`}
                                errorMessage={
                                  getError(
                                    `action-${actionIndex}-param-tag_ids`,
                                  ) ?? undefined
                                }
                                hasError={Boolean(
                                  getError(
                                    `action-${actionIndex}-param-tag_ids`,
                                  ),
                                )}
                                excludeTagIds={actionRows
                                  .filter(
                                    (row, idx) =>
                                      idx !== actionIndex &&
                                      row.type === 'assign_tag',
                                  )
                                  .flatMap((row) =>
                                    (
                                      row.params.tag_ids ||
                                      row.params.tag_id ||
                                      ''
                                    )
                                      .split(',')
                                      .map((s) => s.trim())
                                      .filter(Boolean),
                                  )}
                                alwaysExcludeTagIds={
                                  isTagTriggerEvent
                                    ? selectedTagConditionRefs
                                    : []
                                }
                              />
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {action.type === 'send_contact_form' && (
                                <p className="text-[11px] text-neutral-110">
                                  Each time this rule runs, a new form invite is
                                  sent. The contact may receive multiple emails
                                  if the rule fires again (e.g. program start
                                  date is rescheduled).
                                </p>
                              )}
                              {(
                                ACTION_SCHEMA_BY_TYPE[action.type]?.params || []
                              ).map((param) => {
                                const paramErrorKey = `action-${actionIndex}-param-${param.key}`;
                                const hasParamError = Boolean(
                                  getError(paramErrorKey),
                                );
                                const paramValue =
                                  action.params[param.key] || '';

                                if (param.control === 'mail_template') {
                                  const selectedTemplate = paramValue
                                    ? mailTemplates.find(
                                        (t) => t.id === paramValue,
                                      )
                                    : null;
                                  return (
                                    <div
                                      key={`param-${actionIndex}-${param.key}`}
                                      className="mt-3 space-y-2"
                                    >
                                      <label className="text-xs text-neutral-130 block font-semibold">
                                        Which template?
                                      </label>
                                      <div className="relative w-full min-w-0">
                                        <Listbox
                                          value={paramValue}
                                          onChange={(value) =>
                                            setActionParamValue(
                                              actionIndex,
                                              param.key,
                                              value,
                                            )
                                          }
                                        >
                                          <Listbox.Button
                                            id={`automation-error-${paramErrorKey}`}
                                            onBlur={registerBlur(paramErrorKey)}
                                            className={classNames(
                                              'text-sm flex w-full items-center gap-2 rounded-md border bg-white px-3 py-2.5 text-left text-neutral-150 shadow-sm focus:outline-none focus:ring-2 focus:ring-secondary-100 focus:ring-offset-1',
                                              hasParamError
                                                ? 'border-red-300'
                                                : 'border-neutral-100',
                                            )}
                                          >
                                            {selectedTemplate && (
                                              <MailIcon
                                                className="h-5 w-5 shrink-0 text-secondary-125"
                                                aria-hidden
                                              />
                                            )}
                                            <span className="min-w-0 flex-1 truncate">
                                              {selectedTemplate?.name ??
                                                'Select Mail template'}
                                            </span>
                                            <ChevronDownIcon
                                              className="text-neutral-130 h-4 w-4 shrink-0"
                                              aria-hidden
                                            />
                                          </Listbox.Button>
                                          <Transition
                                            as={Fragment}
                                            enter="transition ease-out duration-100"
                                            enterFrom="opacity-0"
                                            enterTo="opacity-100"
                                            leave="transition ease-in duration-75"
                                            leaveFrom="opacity-100"
                                            leaveTo="opacity-0"
                                          >
                                            <Listbox.Options className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 w-full min-w-full list-none overflow-auto rounded-md border border-neutral-100 bg-white py-2 shadow-lg focus:outline-none">
                                              <Listbox.Option value="">
                                                {({ active }) => (
                                                  <span
                                                    className={classNames(
                                                      'text-neutral-130 flex cursor-default items-center gap-2 px-3 py-2 font-normal',
                                                      active && 'bg-primary-25',
                                                    )}
                                                  >
                                                    Select Mail template
                                                  </span>
                                                )}
                                              </Listbox.Option>
                                              {mailTemplates
                                                .filter(
                                                  (t) =>
                                                    t.id === paramValue ||
                                                    !actionRows
                                                      .filter(
                                                        (row, idx) =>
                                                          idx !== actionIndex &&
                                                          row.type ===
                                                            'send_template',
                                                      )
                                                      .some(
                                                        (row) =>
                                                          (row.params
                                                            .follow_up_template_id ||
                                                            '') === t.id,
                                                      ),
                                                )
                                                .map((t) => (
                                                  <Listbox.Option
                                                    key={t.id}
                                                    value={t.id}
                                                    className={({ active }) =>
                                                      classNames(
                                                        'flex cursor-default items-center gap-2 px-3 py-2 font-normal text-neutral-150',
                                                        active &&
                                                          'bg-primary-25',
                                                      )
                                                    }
                                                  >
                                                    <MailIcon
                                                      className="h-5 w-5 shrink-0 text-secondary-125"
                                                      aria-hidden
                                                    />
                                                    {t.name ?? 'Untitled'}
                                                  </Listbox.Option>
                                                ))}
                                            </Listbox.Options>
                                          </Transition>
                                        </Listbox>
                                      </div>
                                      {param.helperText && (
                                        <p className="text-[11px] text-neutral-110">
                                          {param.helperText}
                                        </p>
                                      )}
                                      {hasParamError && (
                                        <ErrorMessage>
                                          {getError(paramErrorKey)}
                                        </ErrorMessage>
                                      )}
                                    </div>
                                  );
                                }

                                if (param.control === 'practitioner_form') {
                                  const selectedForm = paramValue
                                    ? practitionerOnlyForms.find(
                                        (f) => f.id === paramValue,
                                      )
                                    : null;
                                  return (
                                    <div
                                      key={`param-${actionIndex}-${param.key}`}
                                      className="mt-3 space-y-2"
                                    >
                                      <label className="text-xs text-neutral-130 block font-semibold">
                                        Which form?
                                      </label>
                                      <div className="relative w-full min-w-0">
                                        <Listbox
                                          value={paramValue}
                                          onChange={(value) =>
                                            setActionParamValue(
                                              actionIndex,
                                              param.key,
                                              value,
                                            )
                                          }
                                        >
                                          <Listbox.Button
                                            id={`automation-error-${paramErrorKey}`}
                                            onBlur={registerBlur(paramErrorKey)}
                                            className={classNames(
                                              'text-sm flex w-full items-center gap-2 rounded-md border bg-white px-3 py-2.5 text-left text-neutral-150 shadow-sm focus:outline-none focus:ring-2 focus:ring-secondary-100 focus:ring-offset-1',
                                              hasParamError
                                                ? 'border-red-300'
                                                : 'border-neutral-100',
                                            )}
                                          >
                                            {selectedForm && (
                                              <DocumentTextIcon
                                                className="h-5 w-5 shrink-0 text-secondary-125"
                                                aria-hidden
                                              />
                                            )}
                                            <span className="min-w-0 flex-1 truncate">
                                              {selectedForm?.name ??
                                                'Select practitioner form'}
                                            </span>
                                            <ChevronDownIcon
                                              className="text-neutral-130 h-4 w-4 shrink-0"
                                              aria-hidden
                                            />
                                          </Listbox.Button>
                                          <Transition
                                            as={Fragment}
                                            enter="transition ease-out duration-100"
                                            enterFrom="opacity-0"
                                            enterTo="opacity-100"
                                            leave="transition ease-in duration-75"
                                            leaveFrom="opacity-100"
                                            leaveTo="opacity-0"
                                          >
                                            <Listbox.Options className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 w-full min-w-full list-none overflow-auto rounded-md border border-neutral-100 bg-white py-2 shadow-lg focus:outline-none">
                                              <Listbox.Option value="">
                                                {({ active }) => (
                                                  <span
                                                    className={classNames(
                                                      'text-neutral-130 flex cursor-default items-center gap-2 px-3 py-2 font-normal',
                                                      active && 'bg-primary-25',
                                                    )}
                                                  >
                                                    Select practitioner form
                                                  </span>
                                                )}
                                              </Listbox.Option>
                                              {practitionerOnlyForms.map(
                                                (f) => (
                                                  <Listbox.Option
                                                    key={f.id}
                                                    value={f.id}
                                                    className={({ active }) =>
                                                      classNames(
                                                        'flex cursor-default items-center gap-2 px-3 py-2 font-normal text-neutral-150',
                                                        active &&
                                                          'bg-primary-25',
                                                      )
                                                    }
                                                  >
                                                    <DocumentTextIcon
                                                      className="h-5 w-5 shrink-0 text-secondary-125"
                                                      aria-hidden
                                                    />
                                                    {f.name ?? 'Untitled'}
                                                  </Listbox.Option>
                                                ),
                                              )}
                                            </Listbox.Options>
                                          </Transition>
                                        </Listbox>
                                      </div>
                                      {formsLoading &&
                                        !providerAssessmentsData && (
                                          <p className="text-[11px] text-neutral-110">
                                            Loading forms…
                                          </p>
                                        )}
                                      {!formsLoading &&
                                        practitionerOnlyForms.length === 0 && (
                                          <p className="text-[11px] text-neutral-110">
                                            No practitioner-only forms in your
                                            library. Create one under Forms.
                                          </p>
                                        )}
                                      {hasParamError && (
                                        <ErrorMessage>
                                          {getError(paramErrorKey)}
                                        </ErrorMessage>
                                      )}
                                    </div>
                                  );
                                }

                                if (param.control === 'select') {
                                  const isSendNotificationRecipient =
                                    action.type === 'send_notification' &&
                                    param.key === 'recipient';
                                  if (isSendNotificationRecipient) {
                                    const RecipientIcon =
                                      paramValue === 'provider'
                                        ? UserGroupIcon
                                        : paramValue === 'client'
                                        ? UserIcon
                                        : null;
                                    return (
                                      <div
                                        key={`param-${actionIndex}-${param.key}`}
                                        className="space-y-1"
                                      >
                                        <label className="text-xs text-neutral-130 block font-semibold">
                                          {param.label}
                                        </label>
                                        <div className="relative w-full">
                                          <Listbox
                                            value={paramValue}
                                            onChange={(value) =>
                                              setActionParamValue(
                                                actionIndex,
                                                param.key,
                                                value,
                                              )
                                            }
                                          >
                                            <Listbox.Button
                                              onBlur={registerBlur(
                                                paramErrorKey,
                                              )}
                                              id={`automation-error-${paramErrorKey}`}
                                              className={classNames(
                                                'text-sm flex w-full items-center gap-2 rounded-md border bg-white px-3 py-2.5 text-left text-neutral-150 shadow-sm focus:outline-none focus:ring-2 focus:ring-secondary-100 focus:ring-offset-1',
                                                hasParamError
                                                  ? 'border-red-300'
                                                  : 'border-neutral-100',
                                              )}
                                            >
                                              {RecipientIcon && (
                                                <RecipientIcon
                                                  className="h-5 w-5 shrink-0 text-secondary-125"
                                                  aria-hidden
                                                />
                                              )}
                                              <span className="min-w-0 flex-1 truncate">
                                                {paramValue === 'provider'
                                                  ? 'Provider team'
                                                  : paramValue === 'client'
                                                  ? 'Client'
                                                  : 'Select recipient'}
                                              </span>
                                              <ChevronDownIcon
                                                className="text-neutral-130 h-4 w-4 shrink-0"
                                                aria-hidden
                                              />
                                            </Listbox.Button>
                                            <Transition
                                              as={Fragment}
                                              enter="transition ease-out duration-100"
                                              enterFrom="opacity-0"
                                              enterTo="opacity-100"
                                              leave="transition ease-in duration-75"
                                              leaveFrom="opacity-100"
                                              leaveTo="opacity-0"
                                            >
                                              <Listbox.Options className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 w-full list-none overflow-auto rounded-md border border-neutral-100 bg-white py-2 shadow-lg focus:outline-none">
                                                {(param.options || []).map(
                                                  (option) => {
                                                    const OptionIcon =
                                                      option.value ===
                                                      'provider'
                                                        ? UserGroupIcon
                                                        : option.value ===
                                                          'client'
                                                        ? UserIcon
                                                        : null;
                                                    return (
                                                      <Listbox.Option
                                                        key={option.value}
                                                        value={option.value}
                                                        className={({
                                                          active,
                                                        }) =>
                                                          classNames(
                                                            'flex cursor-default items-center gap-2 px-3 py-2 font-normal text-neutral-150',
                                                            active &&
                                                              'bg-primary-25',
                                                          )
                                                        }
                                                      >
                                                        {OptionIcon && (
                                                          <OptionIcon
                                                            className="h-5 w-5 shrink-0 text-secondary-125"
                                                            aria-hidden
                                                          />
                                                        )}
                                                        {option.label}
                                                      </Listbox.Option>
                                                    );
                                                  },
                                                )}
                                              </Listbox.Options>
                                            </Transition>
                                          </Listbox>
                                        </div>
                                        {hasParamError && (
                                          <ErrorMessage>
                                            {getError(paramErrorKey)}
                                          </ErrorMessage>
                                        )}
                                      </div>
                                    );
                                  }
                                  return (
                                    <div
                                      key={`param-${actionIndex}-${param.key}`}
                                      className="space-y-1"
                                    >
                                      <label className="text-xs text-neutral-130 block font-semibold">
                                        {param.label}
                                      </label>
                                      <div
                                        className={classNames(
                                          'flex w-full items-center gap-2 rounded-md border bg-white px-3 py-2 shadow-sm',
                                          hasParamError
                                            ? 'border-red-300'
                                            : 'border-neutral-100',
                                        )}
                                      >
                                        <DocumentTextIcon
                                          className="h-5 w-5 shrink-0 text-secondary-125"
                                          aria-hidden
                                        />
                                        <select
                                          value={paramValue}
                                          onChange={(event) =>
                                            setActionParamValue(
                                              actionIndex,
                                              param.key,
                                              event.target.value,
                                            )
                                          }
                                          onBlur={registerBlur(paramErrorKey)}
                                          id={`automation-error-${paramErrorKey}`}
                                          className="text-sm min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-neutral-150 focus:outline-none focus:ring-0 [&::-ms-expand]:hidden"
                                        >
                                          {(param.options || []).map(
                                            (option) => (
                                              <option
                                                key={option.value}
                                                value={option.value}
                                              >
                                                {option.label}
                                              </option>
                                            ),
                                          )}
                                        </select>
                                        <ChevronDownIcon
                                          className="text-neutral-130 pointer-events-none h-4 w-4 shrink-0"
                                          aria-hidden
                                        />
                                      </div>
                                      {hasParamError && (
                                        <ErrorMessage>
                                          {getError(paramErrorKey)}
                                        </ErrorMessage>
                                      )}
                                    </div>
                                  );
                                }

                                if (param.control === 'textarea') {
                                  const isSendNotification =
                                    action.type === 'send_notification';
                                  const isCreateInvoice =
                                    action.type === 'create_invoice';
                                  const useFullWidthStyling =
                                    isSendNotification || isCreateInvoice;
                                  return (
                                    <div
                                      key={`param-${actionIndex}-${param.key}`}
                                      className="space-y-1"
                                    >
                                      <label className="text-xs text-neutral-130 block font-semibold">
                                        {param.label}
                                      </label>
                                      <textarea
                                        rows={3}
                                        value={paramValue}
                                        onChange={(event) =>
                                          setActionParamValue(
                                            actionIndex,
                                            param.key,
                                            event.target.value,
                                          )
                                        }
                                        onBlur={registerBlur(paramErrorKey)}
                                        placeholder={param.placeholder}
                                        id={`automation-error-${paramErrorKey}`}
                                        className={classNames(
                                          'text-sm w-full rounded-md border bg-white px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-secondary-100 focus:ring-offset-1',
                                          useFullWidthStyling
                                            ? 'border-neutral-100'
                                            : 'border-neutral-75',
                                          hasParamError
                                            ? 'border-red-300 focus:border-red-300 focus:ring-red-50'
                                            : 'focus:border-secondary-100',
                                        )}
                                      />
                                      {param.helperText && !hasParamError && (
                                        <p className="text-[11px] text-neutral-110">
                                          {param.helperText}
                                        </p>
                                      )}
                                      {hasParamError && (
                                        <ErrorMessage>
                                          {getError(paramErrorKey)}
                                        </ErrorMessage>
                                      )}
                                    </div>
                                  );
                                }

                                const isSendNotification =
                                  action.type === 'send_notification';
                                const isCreateInvoice =
                                  action.type === 'create_invoice';
                                const useFullWidthStyling =
                                  isSendNotification || isCreateInvoice;
                                return (
                                  <div
                                    key={`param-${actionIndex}-${param.key}`}
                                    className="space-y-1"
                                  >
                                    <label className="text-xs text-neutral-130 block font-semibold">
                                      {param.label}
                                    </label>
                                    <input
                                      type={
                                        param.control === 'number'
                                          ? 'number'
                                          : param.control === 'email'
                                          ? 'email'
                                          : param.control === 'url'
                                          ? 'url'
                                          : 'text'
                                      }
                                      value={paramValue}
                                      onChange={(event) =>
                                        setActionParamValue(
                                          actionIndex,
                                          param.key,
                                          event.target.value,
                                        )
                                      }
                                      onBlur={registerBlur(paramErrorKey)}
                                      placeholder={param.placeholder}
                                      id={`automation-error-${paramErrorKey}`}
                                      className={classNames(
                                        useFullWidthStyling
                                          ? 'text-sm w-full rounded-md border border-neutral-100 bg-white px-3 py-2 text-body focus:outline-none focus:ring-2 focus:ring-secondary-100 focus:ring-offset-1'
                                          : getCompactControlClassName(
                                              paramErrorKey,
                                            ),
                                        hasParamError
                                          ? useFullWidthStyling
                                            ? 'border-red-300 focus:border-red-300 focus:ring-red-50'
                                            : ''
                                          : useFullWidthStyling
                                          ? 'border-neutral-100 focus:border-secondary-100'
                                          : '',
                                      )}
                                    />
                                    {param.helperText && !hasParamError && (
                                      <p className="text-[11px] text-neutral-110">
                                        {param.helperText}
                                      </p>
                                    )}
                                    {hasParamError && (
                                      <ErrorMessage>
                                        {getError(paramErrorKey)}
                                      </ErrorMessage>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </Fragment>
                    ))}
                    <ErrorMessage className="mt-1">
                      {getError('actions')}
                    </ErrorMessage>
                    <Button
                      type="button"
                      theme="primary"
                      size="small"
                      title={
                        actionRows.length >= MAX_ACTIONS
                          ? `Maximum ${MAX_ACTIONS} actions`
                          : 'Add action'
                      }
                      IconComponent={PlusIcon}
                      iconPosition="left"
                      disabled={actionRows.length >= MAX_ACTIONS}
                      onClick={() =>
                        actionRows.length < MAX_ACTIONS &&
                        setActionRows((prev) => [
                          ...prev,
                          {
                            type: ACTION_SCHEMAS_FOR_BUILDER[0].type,
                            params: getDefaultActionParams(
                              ACTION_SCHEMAS_FOR_BUILDER[0].type,
                            ),
                          },
                        ])
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {previewRule && (
          <div className="rounded-xl border-2 border-primary-100 bg-primary-25 p-4 shadow-sm">
            <div className="rounded-md bg-neutral-50 p-2.5">
              <div className="flex flex-wrap items-center gap-2">
                {(() => {
                  const triggerDisplay = getWorkflowTriggerLabel(
                    previewRule,
                    programTags,
                    providerServices,
                  );
                  const actionDisplays = getWorkflowActionDisplays(
                    previewRule,
                    mailTemplates,
                    programTags,
                    practitionerOnlyForms,
                  );
                  return (
                    <>
                      <TriggerBadge
                        label={triggerDisplay.label}
                        icon={triggerDisplay.icon}
                        labelDetail={triggerDisplay.labelDetail}
                        tagName={triggerDisplay.tagName}
                        tagStyles={
                          triggerDisplay.tagColor
                            ? getTagStyles(
                                triggerDisplay.tagColor ?? DEFAULT_TAG_COLOR,
                              )
                            : undefined
                        }
                        andTags={triggerDisplay.andTags}
                      />
                      {parsedTriggerConfig?.time ? (
                        <TimeBadge time={parsedTriggerConfig.time} />
                      ) : null}
                      <span className="text-neutral-120 shrink-0 text-[11px]">
                        →
                      </span>
                      {actionDisplays.length === 0 ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-green-100 bg-green-25 px-2 py-1 text-[11px] text-neutral-150">
                          <MailIcon className="h-3.5 w-3.5 shrink-0 text-green-125" />
                          No action
                        </span>
                      ) : (
                        actionDisplays.map((actionDisplay, idx) => (
                          <ActionBadge
                            key={idx}
                            action={actionDisplay}
                            index={idx}
                          />
                        ))
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <span className="text-sm text-neutral-130 font-medium">Enabled</span>
          <Switch
            checked={rule.enabled ?? true}
            onChange={(checked) => updateRule({ enabled: checked })}
            className={classNames(
              'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-green-100 focus:ring-offset-2',
              rule.enabled ?? true ? 'bg-green-125' : 'bg-neutral-100',
            )}
          >
            <span
              className={classNames(
                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition',
                rule.enabled ?? true ? 'translate-x-5' : 'translate-x-0.5',
              )}
            />
          </Switch>
        </div>

        {submitAttempted && hasAnyValidationErrors && (
          <div className="mb-4 px-4 py-3 text-caption text-red-100">
            Please correct the errors above.
          </div>
        )}

        {saveError && (
          <div className="mb-4 px-4 py-3 text-caption text-red-100">
            {saveError}
          </div>
        )}

        <div className="mt-8 flex items-center justify-between space-x-3 border-t border-neutral-100 pt-6">
          <Button
            title="Cancel"
            theme="secondary"
            size="small"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            title={isNewRule ? 'Create automation' : 'Save Changes'}
            size="small"
            onClick={handleSave}
            disabled={updating}
          >
            {updating
              ? isNewRule
                ? 'Creating...'
                : 'Saving...'
              : isNewRule
              ? 'Create automation'
              : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
